/**
 * Dashboard overview page: summary stats, risk cards, vulnerabilities table, incident chart, Assets, Scans, etc.
 *
 * Backend integration (Django / xSPECTRE API v1):
 * - **API overview:** repository `API_GUIDE.md` (or `http://10.10.20.24/api/v1/docs/`).
 * - **React HTTP layer:** `src/api.js`  all `fetch` calls and JWT handling live there; this file only consumes data.
 * - **Legacy field names:** `getDashboardSummary()` maps v1 KPI JSON into names the UI already expected
 *   (e.g. `critical_count_site`). Change `api.js` if the backend renames fields.
 * - **Scan rows:** `normalizeScansRow` / `normalizeNetworkType` map API objects  table columns; extend those
 *   if serializers add fields (keep `BACKEND_INTEGRATION.md` in sync when you change mappings).
 *
 * Data loading: overview widgets use independent React Query entries so one slow
 * endpoint does not block the others and recent results remain visible while refreshing.
 *
 * Security: API text is rendered as plain text (no dangerouslySetInnerHTML).
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import {
  getDashboardSummary,
  supportsOverviewDateRange,
  getTopOpenVulnerabilities,
  getTopVulnerableHosts,
  getDashboardVulnerabilities,
  getHistoryData,
  getInventory,
  getProfile,
  getCachedProfile,
  createUserAdmin,
  listOrganizations,
  createSite,
  updateSite,
  getSites,
  submitNetworkScanSchedule,
  submitWebApplicationScanSchedule,
  getSubscriptionOrganizations,
  getSubscriptionCurrent,
  getSubscriptionUsage,
  hasUsableAccessToken,
  isActiveRunningScan,
  toUserFacingErrorMessage,
  createZapConnector,
  getZapConnector,
  updateZapConnector,
  deleteZapConnector,
  testZapConnector,
  deleteOpenvasConnector,
  testOpenvasConnector,
} from '../api';
import ProfileSettings from '../features/settings/profile/ProfileSettings';
import { useRunningScansQuery } from '../features/scans/hooks/useScansQueries';
import HealthScore from '../features/dashboard-home/components/HealthScore';
import HeatmapChart, {
  transformHistoryToIncidents,
  OVERVIEW_DATE_RANGE_OPTIONS,
  DEFAULT_OVERVIEW_DATE_RANGE,
  overviewDateRangeToApiRange,
} from '../features/dashboard-home/components/HeatmapChart';
import {
  flattenOverviewFindingPacks,
  buildOverviewWidgetsFromFindings,
  buildOverviewWidgetsFromTrend,
  buildTopHostsFromOpenFindings,
  enrichTopHostsWithInventory,
} from '../features/dashboard-home/overviewDateFilter';
import {
  applyCachedDetectionDates,
  countDatedFindings,
} from '../features/dashboard-home/vulnDetectionDateCache';
import SupportPage from '../features/support/SupportPage';
import RisksPageContent, { RisksDetected } from '../features/vulnerabilities/components/VulnerabilitiesPage';
import AssetsTable, { VulnerabilitiesTable, buildDashboardVulnRows, buildAssetTableRows, universalSearchTextMatches, UNIVERSAL_SEARCH_RESULTS_CAP } from '../features/assets/components/AssetInventory';
import PartnerScoreCard from './PartnerScoreCard';
import PartnerManagementSettingsPage from './PartnerManagementSettingsPage';
import DashboardPageContentRouter from '../pages/dashboard/DashboardPageContentRouter';
import './DashboardPage.css';
import '../features/assets/assets.css';
import {
  APP_SETTINGS_PAGES,
  DASHBOARD_PAGE_DEFAULT,
  pathToPage,
  normalizePageKey,
  pageToPath,
  isAppSettingsPage,
  isTableFillPage,
  buildRisksPathWithFilters,
  parseRisksFiltersFromSearchParams,
  buildActiveScansPath,
  buildScansPathWithFilter,
  parseActiveScansStatusFromSearchParams,
  parseScansFilterFromSearchParams,
} from '../routes/dashboardRoutes';
import RiskDetailPageContent from './RiskDetailPageContent';
import UsersOrgsSettingsPage, { normalizeUsersOrgsOrganizations, normalizeUserRoleForForm, USER_DETAIL_ROLE_OPTIONS } from '../features/settings/users-orgs/UsersOrgsSettingsPage';
import UserScopeAssignFields, {
  EMPTY_USER_SCOPE_FORM,
  buildUserScopeCreatePayload,
  hasUserScopeSelection,
} from '../features/settings/users-orgs/UserScopeAssignFields';
import ScansPageContent, { getPreferredTimezoneDefault, NetworkScanManagerModalBody, WebApplicationScanManagerModalBody } from '../features/scans/components/ScansPage';
import ConnectorsSettingsPage, {
  isPermissionDeniedError,
  getZapConnectorInitialState,
  cloneZapAssignmentRows,
  sanitizeZapConnectorField,
  CONNECTORS_SETTINGS_HINT,
} from '../features/settings/connectors/ConnectorsSettingsPage';
import NetworkScannerSettingsForm from '../features/settings/connectors/NetworkScannerSettingsForm';
import ConnectorPasswordField from '../features/settings/connectors/ConnectorPasswordField';
import { emptyDisplayValue, ZAP_SCANNER_TYPES } from '../features/settings/connectors/connectorFormUtils';
import DeploymentSettingsPage from '../features/settings/deployment/DeploymentSettingsPage';
import GhostCveExposuresSystemPage from '../features/ghost-cve/components/GhostCveExposuresSystemPage';
import EarlyDisclosuresPage from '../features/early-disclosure/components/EarlyDisclosuresPage';
import Sidebar, { isXdrNavPage, APP_SETTINGS_NAV } from '../layouts/Sidebar';
import { useThemeToggle } from '../shared/hooks/useThemeToggle';
import { useNotifications } from '../shared/hooks/useNotifications';
import { useUniversalSearch, sanitizeUniversalSearchInput, UNIVERSAL_SEARCH_MAX_LEN } from '../shared/hooks/useUniversalSearch';
import { useScopeSelector } from '../shared/hooks/useScopeSelector';
import { useDashboardTour } from '../features/onboarding/useDashboardTour';
import MfaEnrollmentPrompt, { useMfaEnrollmentPrompt } from '../features/settings/profile/MfaEnrollmentPrompt';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);
// Guided tours: entry lives in the profile menu (under Profile settings).
// TEMP: off for slow rollout — set true to restore menu entry + auto-start prompts.
const DASHBOARD_TOURS_ENABLED = false;

/** Sidebar becomes an off-canvas drawer at this width (must match DashboardPage.css). */
/** Keep in sync with drawer breakpoint in `layouts/sidebar.css` (@media max-width: 1200px). */
const DASHBOARD_MOBILE_NAV_MAX_PX = 1200;

function decodeRouteId(pathname, pattern) {
  const match = String(pathname || '').match(pattern);
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch (_) {
    return match[1];
  }
}

/* SidebarIcons, nav constants, and Sidebar component extracted to ../layouts/Sidebar.jsx */

function normalizeUiRole(profile) {
  if (profile?.is_superuser === true) return 'superuser';

  const rawRole = profile?.role;
  const roleStr = String(
    typeof rawRole === 'string'
      ? rawRole
      : (rawRole?.name ?? rawRole?.label ?? rawRole?.role ?? rawRole ?? '')
  ).trim().toLowerCase();

  if (!roleStr) return 'viewer';
  if (roleStr === 'superuser' || roleStr === 'root') return 'superuser';
  if (roleStr === 'partner' || roleStr === '4') return 'partner';
  if (roleStr === 'admin' || roleStr === 'administrator' || roleStr === '1') return 'admin';
  if (roleStr === 'analyst' || roleStr === '2') return 'analyst';
  if (roleStr === 'viewer' || roleStr === '3') return 'viewer';
  return roleStr;
}

const EMPTY_USER_PROFILE = {
  name: '',
  email: '',
  organizationName: '',
  organization_id: '',
  role: '',
  timezone: getPreferredTimezoneDefault(),
  is_superuser: false,
  capabilities: null,
  uu_id: '',
};

function mapApiProfileToUserProfile(data, prev = EMPTY_USER_PROFILE) {
  if (!data || typeof data !== 'object') return prev;
  const name = String(data?.name ?? data?.display_name ?? '').trim();
  const email = String(data?.email ?? '').trim();
  const organizationName = String(
    data?.organization_name ??
    data?.org_name ??
    data?.organization?.name ??
    data?.company_name ??
    ''
  ).trim();
  const organizationId = String(data?.organization_id ?? data?.organization?.id ?? '').trim();
  const roleRaw = data?.role;
  const roleValue = String(
    typeof roleRaw === 'string'
      ? roleRaw
      : (roleRaw?.name ?? roleRaw?.label ?? roleRaw?.role ?? roleRaw ?? '')
  ).trim();
  const roleLookup = { '1': 'Admin', '2': 'Analyst', '3': 'Viewer', '4': 'Partner' };
  const role = roleLookup[roleValue] || roleValue || '';
  const timezone = String(data?.timezone || '').trim() || getPreferredTimezoneDefault();
  const uuId = String(data?.uu_id ?? data?.user_id ?? data?.id ?? prev?.uu_id ?? '').trim();
  if (!name && !email && !organizationName && !role && data?.is_superuser !== true) return prev;
  return {
    ...prev,
    name: name || prev.name || '',
    email: email || prev.email || '',
    organizationName: organizationName || prev.organizationName || '',
    organization_id: organizationId || prev.organization_id || '',
    role: role || prev.role || '',
    timezone,
    uu_id: uuId || prev.uu_id || '',
    is_superuser: data?.is_superuser === true,
    capabilities: data?.capabilities && typeof data.capabilities === 'object'
      ? data.capabilities
      : (prev.capabilities || null),
  };
}

function buildInitialUserProfile() {
  try {
    return mapApiProfileToUserProfile(getCachedProfile(), EMPTY_USER_PROFILE);
  } catch {
    return { ...EMPTY_USER_PROFILE };
  }
}

function buildUiAccessPolicy(profile) {
  const role = normalizeUiRole(profile);
  const isSuperuser = role === 'superuser';
  const isPartner = role === 'partner';
  const effectiveCaps = profile?.capabilities?.effective_capabilities || {};
  const profileCaps = profile?.capabilities || {};
  const hasCapability = (key) =>
    profileCaps?.[key] === true || effectiveCaps?.[key] === true;

  const canManageUsers =
    isSuperuser ||
    isPartner ||
    effectiveCaps.can_manage_users === true ||
    role === 'admin';

  const canManageOrganizations =
    isSuperuser ||
    isPartner ||
    role === 'admin' ||
    effectiveCaps.can_manage_organizations === true;

  const canSeeSettingsConnectors = isSuperuser || isPartner || hasCapability('can_access_connectors');
  const canManageSettingsConnectors = isSuperuser || isPartner || hasCapability('can_manage_connectors');
  const canSeeSettingsDeployment = isSuperuser;
  const canSeeSettingsPartners = isSuperuser;
  const canRunScans =
    isSuperuser ||
    role === 'partner' ||
    role === 'admin' ||
    role === 'analyst' ||
    effectiveCaps.can_run_scans === true ||
    hasCapability('can_run_scans');

  return {
    role,
    isSuperuser,
    isPartner,
    canRunScans,
    canSeeSubscription: !isPartner && (isSuperuser || role === 'admin'),
    canSeePartnerUi: isPartner,
    canSeeSettingsConnectors,
    canManageSettingsConnectors,
    canSeeSettingsDeployment,
    canSeeSettingsPartners,
    canSeeSettingsManageUsers: canManageUsers,
    canSeeSettingsUsersOrgs: canManageOrganizations,
    // Scan egress settings (allow-list IPs, route, request rate): org admins and above.
    canSeeSettingsScanNetwork: isSuperuser || role === 'admin' || role === 'partner',
    // Staff console: global admins and xSPECTRE (master organization) analysts and above. The API enforces it.
    canSeeSettingsScanNetworkOps: isSuperuser || (String(profile?.organization_id || '') === '1' && (role === 'admin' || role === 'analyst')),
  };
}


function ManageUsersPage({
  userProfile,
  onProfileChange,
  onAddInviteUser,
  usersRefreshKey,
  onLogout,
  headerSelectedOrgId = '',
  headerSelectedOrgName = '',
}) {
  return (
    <ProfileSettings
      userProfile={userProfile}
      onProfileChange={onProfileChange}
      forcedTab="manage_users"
      hideProfileChrome
      onAddInviteUser={onAddInviteUser}
      usersRefreshKey={usersRefreshKey}
      onLogout={onLogout}
      headerSelectedOrgId={headerSelectedOrgId}
      headerSelectedOrgName={headerSelectedOrgName}
    />
  );
}

/* Risk/asset components imported from features/ */

function TourMenuIcon({ id }) {
  const icon = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.25, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (id) {
    case 'overview':
      return (
        <svg {...icon}>
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
        </svg>
      );
    case 'assets':
      return (
        <svg {...icon}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      );
    case 'scans-web':
      return (
        <svg {...icon}>
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      );
    case 'scans-network':
      return (
        <svg {...icon}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="8.5" y="14" width="7" height="7" rx="1.5" />
          <path d="M6.5 10v2.5A1.5 1.5 0 0 0 8 14h1" />
          <path d="M17.5 10v2.5A1.5 1.5 0 0 1 16 14h-1" />
        </svg>
      );
    case 'vulnerabilities':
      return (
        <svg {...icon}>
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
      );
    case 'reports':
      return (
        <svg {...icon}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...icon}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        </svg>
      );
    case 'subscription':
      return (
        <svg {...icon}>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      );
    case 'support':
      return (
        <svg {...icon}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    default:
      return (
        <svg {...icon}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 1 1 4.8 2.4c-1.2.8-1.9 1.4-1.9 2.6" />
          <path d="M12 18h.01" />
        </svg>
      );
  }
}

/**
 * Main dashboard page: loads all data from backend (or fallbacks), then renders layout.
 *
 * @param {{
 *   onLogout?: () => void | Promise<void>,
 *   forcedPage?: string | null
 * }} props  optional; profile menu "Log out" calls `onLogout` so `App.js` can clear JWT state.
 * `forcedPage` lets route wrappers pin the rendered section during incremental route extraction.
 */
/** Full-page loader only when this overview request has nothing to paint yet. */
function overviewBlocksPage(query, scopeReady) {
  if (query?.data != null && !query.isPlaceholderData) return false;
  return !scopeReady || Boolean(query?.isPending || query?.isFetching || query?.isPlaceholderData);
}

export default function DashboardPage({ onLogout, forcedPage = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { routeRiskId, routeReportId, routeScanId } = useMemo(() => ({
    routeRiskId: decodeRouteId(location.pathname, /^\/app\/risks\/([^/]+)\/?$/i),
    routeReportId: decodeRouteId(location.pathname, /^\/(?:app\/)?dashboard\/reports\/([^/]+)\/?$/i)
      || decodeRouteId(location.pathname, /^\/app\/reports\/([^/]+)\/?$/i),
    routeScanId: decodeRouteId(location.pathname, /^\/app\/scans\/(?!activity(?:\/|$))([^/]+)\/?$/i),
  }), [location.pathname]);
  const [searchParams] = useSearchParams();
  const forcedPageKey = useMemo(
    () => (forcedPage ? normalizePageKey(forcedPage) : null),
    [forcedPage]
  );
  const activePageFromPath = useMemo(() => pathToPage(location.pathname), [location.pathname]);
  const activePage = useMemo(() => {
    if (forcedPageKey) return forcedPageKey;
    if (activePageFromPath) return normalizePageKey(activePageFromPath);
    return DASHBOARD_PAGE_DEFAULT;
  }, [activePageFromPath, forcedPageKey]);
  const routeScannerHint = searchParams.get('scanner') || '';
  const routeScanTypeHint = searchParams.get('type') || '';
  const [assetsInventory, setAssetsInventory] = useState(null);
  const [assetsDataWarning, setAssetsDataWarning] = useState('');
  const [, setAssetsLoading] = useState(false);
  const assetsInventoryScopeKeyRef = useRef(null);
  const assetsFetchedAtRef = useRef(0);
  const assetsInventoryRef = useRef(assetsInventory);
  assetsInventoryRef.current = assetsInventory;
  const {
    orgs, selectedOrgId, setSelectedOrgId, selectedSiteId, setSelectedSiteId,
    dashboardScopeQuery, earlyIntelScopeQuery, scopeReady,
    orgDropdownOpen, setOrgDropdownOpen, orgSearch, setOrgSearch,
    orgsLoading, orgsError, orgsHint,
    siteDropdownOpen, setSiteDropdownOpen, siteSearch, setSiteSearch,
    headerSites, headerSitesLoading,
    getOrgIdentity, headerSelectedOrgName, filteredHeaderOrgs, filteredHeaderSites,
    handleOrgSelect, handleClearOrgSelection, handleSiteSelect, loadHeaderSites,
  } = useScopeSelector();

  // Queries pause off the dashboard. Cached results still paint immediately on return.
  const [overviewDateRange, setOverviewDateRange] = useState(DEFAULT_OVERVIEW_DATE_RANGE);
  const [overviewDateRangeOpen, setOverviewDateRangeOpen] = useState(false);
  const overviewQueriesEnabled = activePage === 'dashboard' && scopeReady;
  const overviewApiRange = overviewDateRangeToApiRange(overviewDateRange);
  // Always send ?range=; when portal deploys friend's API we trust it, otherwise
  // fall back to client windowing until supportsOverviewDateRange() says yes.
  const overviewApiQuery = useMemo(() => {
    const base =
      dashboardScopeQuery && typeof dashboardScopeQuery === 'object'
        ? { ...dashboardScopeQuery }
        : {};
    delete base.days;
    base.range = overviewApiRange;
    return base;
  }, [dashboardScopeQuery, overviewApiRange]);
  const overviewScopeKey = useMemo(
    () => [
      String(overviewApiQuery?.org_id || 'all'),
      String(overviewApiQuery?.site_uuid || 'all'),
      String(overviewApiQuery?.range || 'all'),
    ],
    [overviewApiQuery]
  );
  const overviewQueryOptions = {
    enabled: overviewQueriesEnabled,
    staleTime: 3 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  };

  const overviewRangeSupportQuery = useQuery({
    queryKey: ['dashboard-overview', 'range-support'],
    queryFn: () => supportsOverviewDateRange(),
    enabled: overviewQueriesEnabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });
  const backendSupportsOverviewRange = overviewRangeSupportQuery.data === true;
  // Client fallback only while the live API still ignores `range`.
  const useClientOverviewDateFilter =
    overviewApiRange !== 'all' && !backendSupportsOverviewRange;

  const summaryQuery = useQuery({
    queryKey: ['dashboard-overview', 'summary', ...overviewScopeKey],
    queryFn: () => getDashboardSummary(null, overviewApiQuery),
    ...overviewQueryOptions,
  });
  // Baseline all-time KPIs for client-side trend scaling (pre-deploy only).
  const summaryAllTimeQuery = useQuery({
    queryKey: ['dashboard-overview', 'summary-all', overviewScopeKey[0], overviewScopeKey[1]],
    queryFn: () => {
      const scope = { ...overviewApiQuery, range: 'all' };
      return getDashboardSummary(null, scope);
    },
    enabled: overviewQueriesEnabled && useClientOverviewDateFilter,
    staleTime: 3 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
  const overviewFindingsQuery = useQuery({
    queryKey: ['dashboard-overview', 'findings-window', overviewScopeKey[0], overviewScopeKey[1]],
    queryFn: async () => {
      const scope = { ...overviewApiQuery };
      delete scope.range;
      const [open, closed, accepted] = await Promise.all([
        getDashboardVulnerabilities({ ...scope, status: 'open' }),
        getDashboardVulnerabilities({ ...scope, status: 'closed' }),
        getDashboardVulnerabilities({ ...scope, status: 'accepted' }),
      ]);

      let packs = flattenOverviewFindingPacks({ open, closed, accepted });
      const openDatedCount = countDatedFindings(packs.openRows);
      if (packs.openRows.length > 0 && openDatedCount / packs.openRows.length < 0.95) {
        const cached = await applyCachedDetectionDates(packs.openRows);
        packs = { ...packs, openRows: cached.rows, dateCoverage: cached.coverage };
      } else {
        packs.dateCoverage = packs.openRows.length
          ? openDatedCount / packs.openRows.length
          : 1;
      }
      return packs;
    },
    enabled: overviewQueriesEnabled && useClientOverviewDateFilter,
    staleTime: 3 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
  // Load up to 1y of trend history once. The chart's own filter windows that
  // series client-side — it must not refetch or rewrite Overview KPIs / tables.
  const historyApiQuery = useMemo(() => {
    const base =
      dashboardScopeQuery && typeof dashboardScopeQuery === 'object'
        ? { ...dashboardScopeQuery }
        : {};
    delete base.days;
    base.range = '1y';
    return base;
  }, [dashboardScopeQuery]);
  const historyQuery = useQuery({
    queryKey: [
      'dashboard-overview',
      'history',
      String(historyApiQuery?.org_id || 'all'),
      String(historyApiQuery?.site_uuid || 'all'),
      '1y',
    ],
    queryFn: async () => transformHistoryToIncidents(
      await getHistoryData(null, historyApiQuery)
    ),
    ...overviewQueryOptions,
  });
  const chartIncidents = Array.isArray(historyQuery.data) ? historyQuery.data : null;
  const overviewDerived = useMemo(() => {
    if (!useClientOverviewDateFilter) return null;
    const fromFindings = overviewFindingsQuery.data
      ? buildOverviewWidgetsFromFindings(overviewFindingsQuery.data, overviewApiRange, {
          minDateCoverage: 0.95,
        })
      : null;
    if (fromFindings && !fromFindings.unsupported && fromFindings.summaryPatch) {
      return { ...fromFindings, source: 'findings' };
    }
    // Portal list often omits date_time — fall back to trend-window totals for Risks.
    if (Array.isArray(chartIncidents) && chartIncidents.length > 0) {
      return buildOverviewWidgetsFromTrend(
        chartIncidents,
        overviewApiRange,
        summaryAllTimeQuery.data || summaryQuery.data
      );
    }
    return fromFindings;
  }, [
    useClientOverviewDateFilter,
    overviewFindingsQuery.data,
    overviewApiRange,
    chartIncidents,
    summaryAllTimeQuery.data,
    summaryQuery.data,
  ]);
  const useDerivedOverviewSummary = Boolean(
    overviewDerived && !overviewDerived.unsupported && overviewDerived.summaryPatch
  );
  const useDerivedOverviewTables = Boolean(
    useDerivedOverviewSummary
    && overviewDerived?.source === 'findings'
    && Array.isArray(overviewDerived?.topHosts)
    && Array.isArray(overviewDerived?.topVulnerabilities)
  );
  const vulnerabilitiesQuery = useQuery({
    queryKey: ['dashboard-overview', 'top-vulnerabilities', ...overviewScopeKey],
    queryFn: () => getTopOpenVulnerabilities(null, overviewApiQuery),
    refetchOnWindowFocus: true,
    ...overviewQueryOptions,
    enabled: overviewQueriesEnabled && !useDerivedOverviewTables,
  });
  // Counts from the same open findings Risks uses; hostname/uu_id enriched from inventory.
  const hostsQuery = useQuery({
    queryKey: ['dashboard-overview', 'top-hosts-from-risks', ...overviewScopeKey],
    queryFn: async () => {
      const [data, inventoryHosts] = await Promise.all([
        getDashboardVulnerabilities({
          ...overviewApiQuery,
          status: 'open',
        }),
        getTopVulnerableHosts(null, overviewApiQuery).catch(() => []),
      ]);
      const { openRows } = flattenOverviewFindingPacks({ open: data });
      const aggregated = buildTopHostsFromOpenFindings(openRows, 10);
      return enrichTopHostsWithInventory(aggregated, inventoryHosts);
    },
    ...overviewQueryOptions,
    enabled: overviewQueriesEnabled,
  });
  const runningScansQuery = useRunningScansQuery({
    orgQuery: dashboardScopeQuery,
    enabled: overviewQueriesEnabled,
  });

  const summary = useMemo(() => {
    const base = summaryQuery.data || null;
    if (!useDerivedOverviewSummary) return base;
    if (!base) return { ...overviewDerived.summaryPatch };
    return {
      ...base,
      ...overviewDerived.summaryPatch,
    };
  }, [summaryQuery.data, overviewDerived, useDerivedOverviewSummary]);
  const vulnerabilities = useDerivedOverviewTables
    ? overviewDerived.topVulnerabilities
    : (Array.isArray(vulnerabilitiesQuery.data) ? vulnerabilitiesQuery.data : null);
  const topVulnerableHosts = Array.isArray(hostsQuery.data) ? hostsQuery.data : null;
  const dataWarnings = useMemo(() => {
    const warnings = [];
    const addError = (label, query, suppressLicenseGate = false) => {
      if (!query.error) return;
      const msg = toUserFacingErrorMessage(query.error?.message, 'request failed');
      if (suppressLicenseGate && /enterprise|license|subscription|upgrade|plan required/i.test(msg)) {
        return;
      }
      warnings.push(`${label} unavailable: ${msg}`);
    };
    addError('Overview KPIs', summaryQuery);
    if (useClientOverviewDateFilter) {
      addError('Overview date window', overviewFindingsQuery, true);
    }
    if (!useDerivedOverviewTables) {
      addError('Top vulnerabilities', vulnerabilitiesQuery, true);
      addError('Top vulnerable hosts', hostsQuery);
    }
    addError('Trend history', historyQuery, true);
    if (summary?._dashboardKpisError) {
      warnings.push(
        `Overview KPIs: ${toUserFacingErrorMessage(summary._dashboardKpisError, 'Unable to load overview metrics.')}`
      );
    }
    if (summary?._dashboardKpisUnauthorized && !hasUsableAccessToken()) {
      warnings.push(
        'Live health score was not loaded. Please sign in and try again.'
      );
    }
    if (assetsDataWarning) warnings.push(assetsDataWarning);
    return warnings;
  }, [
    assetsDataWarning,
    historyQuery.error,
    hostsQuery.error,
    overviewFindingsQuery.error,
    summary,
    summaryQuery.error,
    useClientOverviewDateFilter,
    useDerivedOverviewTables,
    vulnerabilitiesQuery.error,
  ]);

  // Header scan status: backend fields in_progress_scans, scheduled_scans (DashboardSummaryView).
  const fallbackScanStatus = { inProgress: 0, scheduled: 0 };
  const liveRunningScans = Array.isArray(runningScansQuery.data)
    ? runningScansQuery.data.filter(isActiveRunningScan)
    : null;
  const inProgressScans = liveRunningScans
    ? liveRunningScans.length
    : summary?.in_progress_scans !== undefined && summary?.in_progress_scans !== null
      ? Number(summary.in_progress_scans) || 0
      : fallbackScanStatus.inProgress;
  const scheduledScans =
    summary?.scheduled_scans !== undefined && summary?.scheduled_scans !== null
      ? Number(summary.scheduled_scans) || 0
      : fallbackScanStatus.scheduled;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${DASHBOARD_MOBILE_NAV_MAX_PX}px)`).matches
  );
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !window.matchMedia(`(max-width: ${DASHBOARD_MOBILE_NAV_MAX_PX}px)`).matches;
  });
  const { isDarkMode, toggleDarkMode } = useThemeToggle();
  // UI state: show settings dropdown (restored from activePage, not localStorage)
  const [settingsNavOpen, setSettingsNavOpen] = useState(() => {
    try {
      const currentPage = pathToPage(window.location.pathname);
      if (currentPage && isAppSettingsPage(currentPage)) return true;
      return false;
    } catch (_) {
      return false;
    }
  });
  const [scansNavOpen, setScansNavOpen] = useState(() => {
    try {
      const currentPage = pathToPage(window.location.pathname);
      return currentPage === 'scans' || currentPage === 'scanDetail';
    } catch (_) {
      return false;
    }
  });
  const [xdrNavOpen, setXdrNavOpen] = useState(() => {
    try {
      const currentPage = pathToPage(window.location.pathname);
      return currentPage ? isXdrNavPage(currentPage) : false;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    const normalizedPath = String(location.pathname || '/').replace(/\/+$/, '') || '/';
    if (normalizedPath === '/app') {
      // On fresh mount or /app access, always go to dashboard (not to saved settings page from localStorage)
      // This ensures post-login redirects to /app/dashboard, not random settings pages
      navigate(pageToPath(DASHBOARD_PAGE_DEFAULT), { replace: true });
      return;
    }
    // Legacy Activities page → Scans list with the same status filter.
    if (normalizedPath === '/app/scans/activity') {
      const status = parseScansFilterFromSearchParams(location.search).status || 'all';
      navigate(buildScansPathWithFilter({ status, tab: 'web' }), { replace: true });
      return;
    }
    if (!activePageFromPath) {
      navigate(pageToPath(DASHBOARD_PAGE_DEFAULT), { replace: true });
    }
  }, [activePageFromPath, location.pathname, location.search, navigate]);
  const {
    universalSearchQuery, setUniversalSearchQuery,
    universalSearchDropdownOpen, setUniversalSearchDropdownOpen,
  } = useUniversalSearch();
  /** Bumps when navigating to Ghost CVE Exposures so filters follow sidebar (clear) vs Ghost CVEs row View (CVE query). */
  const [ghostExposuresFilterSeed, setGhostExposuresFilterSeed] = useState({ nonce: 0, cveQuery: '' });
  /**
   * New Scan flow: two-pane horizontal slide inside `#new-scan-flow-dialog`.
   * Steps: `pick-type` (Web vs Network)  `web-manager` | `network-manager`.
   * `scanManagerFormKey` increments when moving pick  manager so forms remount with fresh `getNetworkScanScheduleDefaults()`.
   */
  const [newScanModalOpen, setNewScanModalOpen] = useState(false);
  const [newScanSkipPickType, setNewScanSkipPickType] = useState(false);
  const [createSiteModalOpen, setCreateSiteModalOpen] = useState(false);
  const [createSiteName, setCreateSiteName] = useState('');
  const [createSiteDescription, setCreateSiteDescription] = useState('');
  const [createSiteOrganizationId, setCreateSiteOrganizationId] = useState('');
  const [createSiteModalError, setCreateSiteModalError] = useState('');
  const [createSiteSubmitting, setCreateSiteSubmitting] = useState(false);
  const [editSiteModalOpen, setEditSiteModalOpen] = useState(false);
  const [editSiteSubmitting, setEditSiteSubmitting] = useState(false);
  const [editSiteModalError, setEditSiteModalError] = useState('');
  const [editSiteCanChangeOrg, setEditSiteCanChangeOrg] = useState(false);
  const [editSiteForm, setEditSiteForm] = useState({
    uuId: '',
    organizationId: '',
    siteName: '',
    siteDescription: '',
    siteDetails: '',
    siteStatus: '',
    isActive: true,
  });
  /** Connectors page: + Add Connector opens the connector creation flow. */
  const [addConnectorModalOpen, setAddConnectorModalOpen] = useState(false);
  /** Active Connectors table tab: `web` | `network`. Add Connector opens that scanner form. */
  const [connectorsScannerTab, setConnectorsScannerTab] = useState('web');
  /** `pick-scanner`  Web vs Network; `web-zap`  ZAP settings; `network-openvas`  OpenVAS / GVM settings. */
  const [addConnectorStep, setAddConnectorStep] = useState('pick-scanner');
  const [zapConnectorDraft, setZapConnectorDraft] = useState(getZapConnectorInitialState);
  const [zapConnectorSnapshot, setZapConnectorSnapshot] = useState(getZapConnectorInitialState);
  const [showZapApiKey, setShowZapApiKey] = useState(false);
  const [showZapSshPassword, setShowZapSshPassword] = useState(false);
  const [zapPoolOrgOptions, setZapPoolOrgOptions] = useState([]);
  const [zapAssignmentRows, setZapAssignmentRows] = useState(cloneZapAssignmentRows);
  const [zapAssignmentSnapshot, setZapAssignmentSnapshot] = useState(cloneZapAssignmentRows);
  const [zapAssignmentSearch, setZapAssignmentSearch] = useState('');
  const [openvasConnectorEditId, setOpenvasConnectorEditId] = useState(null);
  /** UUID of the ZAP connector being edited; null when creating new. */
  const [zapConnectorEditId, setZapConnectorEditId] = useState(null);
  /** Existing ZAP connectors open ready to edit (Cancel reverts to the loaded snapshot). */
  const [zapConnectorIsEditing, setZapConnectorIsEditing] = useState(true);
  const [zapConnectorSaving, setZapConnectorSaving] = useState(false);
  const [zapConnectorSaveError, setZapConnectorSaveError] = useState('');
  const [zapConnectorFieldErrors, setZapConnectorFieldErrors] = useState({ name: '', rportClient: '' });
  const [zapSitesLoading, setZapSitesLoading] = useState(false);
  /** Incrementing key forces ConnectorsSettingsPage to re-load the list. */
  const [connectorListRefreshKey, setConnectorListRefreshKey] = useState(0);
  /** Guards async Configure loads so a late response cannot overwrite Add/create state. */
  const connectorModalSessionRef = useRef(0);
  const [connectorDeleteConfirm, setConnectorDeleteConfirm] = useState(null);
  const [connectorDeleteBusy, setConnectorDeleteBusy] = useState(false);
  const [newScanStep, setNewScanStep] = useState('pick-type');
  const [scanManagerFormKey, setScanManagerFormKey] = useState(0);
  const prevNewScanStepRef = useRef('pick-type');
  /** When set from the New Scan dialog, Scans page applies this tab once then clears via `consumeScansInitialTab`. */
  const [scansInitialTab, setScansInitialTab] = useState(null);
  const [scansInitialFilter, setScansInitialFilter] = useState('all');
  const [scansRefreshKey, setScansRefreshKey] = useState(0);
  const risksFilterSeed = useMemo(
    () => parseRisksFiltersFromSearchParams(searchParams),
    [searchParams]
  );
  const scansFilterSeed = useMemo(
    () => parseScansFilterFromSearchParams(searchParams),
    [searchParams]
  );
  const scansPageStatusFilter = scansFilterSeed.status !== 'all'
    ? scansFilterSeed.status
    : scansInitialFilter;
  const scansPageTab = scansFilterSeed.tab || scansInitialTab;

  // Subscription state  loaded from real backend API.
  const [subscriptionOrgs, setSubscriptionOrgs] = useState([]);
  const [subscriptionOrgsLoading, setSubscriptionOrgsLoading] = useState(false);
  const [subscriptionOrgsError, setSubscriptionOrgsError] = useState('');
  const [selectedSubscriptionOrg, setSelectedSubscriptionOrg] = useState(null);
  const [subscriptionCurrent, setSubscriptionCurrent] = useState(null);
  const [subscriptionCurrentLoading, setSubscriptionCurrentLoading] = useState(false);
  const [subscriptionCurrentError, setSubscriptionCurrentError] = useState('');
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [subscriptionUsageLoading, setSubscriptionUsageLoading] = useState(false);
  const [subscriptionUsageError, setSubscriptionUsageError] = useState('');
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [subscriptionSortConfig, setSubscriptionSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [manageUsersInviteModalOpen, setManageUsersInviteModalOpen] = useState(false);
  const [manageUsersRefreshKey, setManageUsersRefreshKey] = useState(0);
  const [manageUsersInviteStep, setManageUsersInviteStep] = useState('choice');
  const [manageUsersInviteSubmitting, setManageUsersInviteSubmitting] = useState(false);
  const [manageUsersInviteError, setManageUsersInviteError] = useState('');
  const [manageUsersInviteFieldErrors, setManageUsersInviteFieldErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [manageUsersInviteOrgs, setManageUsersInviteOrgs] = useState([]);
  const [manageUsersInviteSites, setManageUsersInviteSites] = useState([]);
  const [manageUsersInviteOrgSearch, setManageUsersInviteOrgSearch] = useState('');
  const [manageUsersInviteSiteSearch, setManageUsersInviteSiteSearch] = useState('');
  const [manageUsersInviteForm, setManageUsersInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'Viewer',
    ...EMPTY_USER_SCOPE_FORM,
  });

  // Load subscription organisations when user visits the subscription page.
  // Staging may still return the slim list shape; enrich from /subscription/current/
  // in the background so the table is usable without waiting on N per-org calls.
  useEffect(() => {
    if (activePage !== 'subscription' && activePage !== 'subscriptionOrgDetail') return;
    let mounted = true;
    setSubscriptionOrgsLoading(true);
    setSubscriptionOrgsError('');
    getSubscriptionOrganizations()
      .then(async (data) => {
        if (!mounted) return;
        let orgs = Array.isArray(data?.organizations) ? data.organizations : [];
        // Paint the list as soon as the org catalog returns.
        setSubscriptionOrgs(orgs);
        setSubscriptionOrgsLoading(false);

        const needsEnrichment =
          orgs.length > 0 && !Object.prototype.hasOwnProperty.call(orgs[0] || {}, 'planName');
        if (!needsEnrichment) return;

        // Enrich in small batches — unbounded Promise.all hammered /subscription/current/
        // and kept the page spinner up for 20s+ per org on slow environments.
        const ENRICH_BATCH = 4;
        const enriched = orgs.slice();
        for (let i = 0; i < enriched.length; i += ENRICH_BATCH) {
          if (!mounted) return;
          const batch = enriched.slice(i, i + ENRICH_BATCH);
          const batchResults = await Promise.all(
            batch.map(async (org) => {
              const orgId = String(org?.id || '').trim();
              if (!orgId) return org;
              try {
                const cur = await getSubscriptionCurrent(orgId);
                return {
                  ...org,
                  planName: cur?.planName ?? null,
                  siteCount: cur?.siteCount ?? null,
                  trialExpiry: cur?.trialExpiry ?? null,
                  billingModel: cur?.billingModel ?? null,
                  trialMode: typeof cur?.trialMode === 'boolean' ? cur.trialMode : org.trialMode,
                  ecommerceMode:
                    typeof cur?.ecommerceMode === 'boolean' ? cur.ecommerceMode : org.ecommerceMode,
                };
              } catch {
                return org;
              }
            })
          );
          for (let j = 0; j < batchResults.length; j += 1) {
            enriched[i + j] = batchResults[j];
          }
          if (!mounted) return;
          setSubscriptionOrgs(enriched.slice());
        }
      })
      .catch((err) => {
        if (!mounted) return;
        setSubscriptionOrgsError(err?.message || 'Failed to load organisations.');
        setSubscriptionOrgsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [activePage]);

  // Load subscription detail + usage when an org is selected.
  useEffect(() => {
    const orgId = selectedSubscriptionOrg?.id;
    if (!orgId) return;
    let mounted = true;

    setSubscriptionCurrentLoading(true);
    setSubscriptionCurrentError('');
    getSubscriptionCurrent(orgId)
      .then((data) => { if (mounted) setSubscriptionCurrent(data); })
      .catch((err) => { if (mounted) setSubscriptionCurrentError(err?.message || 'Failed to load subscription.'); })
      .finally(() => { if (mounted) setSubscriptionCurrentLoading(false); });

    setSubscriptionUsageLoading(true);
    setSubscriptionUsageError('');
    getSubscriptionUsage(orgId)
      .then((data) => { if (mounted) setSubscriptionUsage(data); })
      .catch((err) => { if (mounted) setSubscriptionUsageError(err?.message || 'Failed to load usage.'); })
      .finally(() => { if (mounted) setSubscriptionUsageLoading(false); });

    return () => { mounted = false; };
  }, [selectedSubscriptionOrg]);

  // If user lands directly on plan-usage page (e.g. bookmark/refresh) with no
  // org selected, auto-select the first org in the list once it has loaded.
  // When navigating from the subscription list, the org is passed via router state
  // to avoid a React 18 batching race where navigate() causes a re-render before
  // setSelectedSubscriptionOrg has been committed, causing the auto-select to fire.
  useEffect(() => {
    if (activePage !== 'subscriptionOrgDetail') return;
    // Org passed via navigation from subscription list  use it immediately.
    const orgFromNav = location.state?.subscriptionOrg;
    if (orgFromNav) {
      setSelectedSubscriptionOrg(orgFromNav);
      return;
    }
    if (selectedSubscriptionOrg !== null) return;
    if (subscriptionOrgsLoading) return;
    if (!Array.isArray(subscriptionOrgs) || subscriptionOrgs.length === 0) return;
    setSelectedSubscriptionOrg(subscriptionOrgs[0]);
  }, [activePage, selectedSubscriptionOrg, subscriptionOrgs, subscriptionOrgsLoading, location.state]);

  const filteredSubscriptionSites = useMemo(() => {
    const sites = Array.isArray(subscriptionUsage?.sites) ? subscriptionUsage.sites : [];
    const term = subscriptionSearch.trim().toLowerCase();
    const rows = term ? sites.filter((r) => r.name.toLowerCase().includes(term)) : sites.slice();
    const { key, direction } = subscriptionSortConfig;
    const factor = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const aPct = (k) => (a[`${k}Total`] ? a[`${k}Used`] / a[`${k}Total`] : 0);
      const bPct = (k) => (b[`${k}Total`] ? b[`${k}Used`] / b[`${k}Total`] : 0);
      if (key === 'name') return a.name.localeCompare(b.name) * factor;
      if (key === 'perimeter') return (aPct('perimeter') - bPct('perimeter')) * factor;
      if (key === 'onPrem') return (aPct('onPrem') - bPct('onPrem')) * factor;
      if (key === 'web') return (aPct('web') - bPct('web')) * factor;
      if (key === 'plan') {
        return String(a.planName || '').localeCompare(String(b.planName || ''), undefined, { sensitivity: 'base' }) * factor;
      }
      return 0;
    });
    return rows;
  }, [subscriptionSearch, subscriptionSortConfig, subscriptionUsage]);

  const toggleSubscriptionSort = (key) => {
    setSubscriptionSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSubscriptionSortIcon = (key) => {
    if (subscriptionSortConfig.key !== key) return '';
    return subscriptionSortConfig.direction === 'desc' ? '' : '';
  };

  const renderSubscriptionUsageSegments = (used, total, segmentsTotal = 18) => {
    const ratio = total > 0 ? used / total : 0;
    const pct = Math.max(0, Math.min(100, Math.round(ratio * 100)));
    const filled = Math.max(0, Math.min(segmentsTotal, Math.round((pct / 100) * segmentsTotal)));
    const tierClass =
      pct < 50
        ? 'subscription-org-usage-seg--green'
        : pct <= 80
          ? 'subscription-org-usage-seg--orange'
          : 'subscription-org-usage-seg--red';

    return Array.from({ length: segmentsTotal }, (_, i) => (
      <span
        key={i}
        className={`subscription-org-usage-seg ${
          i < filled ? tierClass : 'subscription-org-usage-seg--inactive'
        }`}
      />
    ));
  };

  const consumeScansInitialTab = useCallback(() => {
    setScansInitialTab(null);
  }, []);

  const consumeScansInitialFilter = useCallback(() => {
    setScansInitialFilter('all');
  }, []);

  const navigateToPage = useCallback(
    (page, options = {}) => {
      const normalizedPage = normalizePageKey(page);
      const targetPath = options.path || pageToPath(normalizedPage);
      const currentFull = `${location.pathname}${location.search}`;
      if (targetPath === currentFull && !options.state) return;
      navigate(targetPath, options);
    },
    [location.pathname, location.search, navigate]
  );

  const goBackToRisks = useCallback(() => {
    navigate(pageToPath('risks'));
  }, [navigate]);

  const goBackToScans = useCallback(() => {
    const scanType =
      String(location.state?.scanType || routeScanTypeHint || '').trim().toLowerCase() === 'network'
        ? 'network'
        : 'web';
    const stateLocation = String(location.state?.scanLocation || '').trim().toLowerCase();
    const locationFilter = scanType === 'network'
      ? (stateLocation === 'perimeter' || scansFilterSeed.location === 'perimeter'
        ? 'perimeter'
        : 'onprem')
      : null;
    navigate(buildScansPathWithFilter({ status: 'all', tab: scanType, location: locationFilter }));
  }, [
    location.state?.scanLocation,
    location.state?.scanType,
    navigate,
    routeScanTypeHint,
    scansFilterSeed.location,
  ]);

  const riskDetailSeedRow = location.state?.riskRow || null;
  const scanDetailSeedRow = location.state?.scanRow || null;
  const scanDetailTargetLabel =
    location.state?.targetLabel || scanDetailSeedRow?.target || '';
  const scanDetailType =
    String(location.state?.scanType || routeScanTypeHint || scanDetailSeedRow?.scanType || '')
      .trim()
      .toLowerCase() === 'network'
      ? 'network'
      : 'web';

  const openScansPageWithFilter = useCallback((filter = 'all', tab = 'web', location = null) => {
    const safeFilter = String(filter || 'all').trim().toLowerCase();
    const safeTab = tab === 'network' ? 'network' : 'web';
    const safeLocation = safeTab === 'network'
      ? (String(location || '').trim().toLowerCase() === 'perimeter' ? 'perimeter' : 'onprem')
      : null;
    setScansNavOpen(true);
    setSettingsNavOpen(false);
    setXdrNavOpen(false);
    setScansInitialTab(safeTab);
    navigate(buildScansPathWithFilter({
      status: safeFilter || 'all',
      tab: safeTab,
      location: safeLocation,
    }));
  }, [navigate]);

  const openActiveScansPage = useCallback((status = 'in_progress') => {
    const safeStatus = String(status || 'in_progress').trim().toLowerCase() === 'scheduled'
      ? 'scheduled'
      : 'in_progress';
    setScansNavOpen(false);
    setSettingsNavOpen(false);
    setXdrNavOpen(false);
    navigate(buildActiveScansPath({ status: safeStatus }));
  }, [navigate]);

  const openRisksPageWithFilters = useCallback((filters = {}) => {
    navigate(buildRisksPathWithFilters(filters));
  }, [navigate]);

  const closeNewScanModal = useCallback(() => {
    setNewScanModalOpen(false);
    setNewScanStep('pick-type');
    setNewScanSkipPickType(false);
  }, []);

  const closeAddConnectorModal = useCallback(() => {
    connectorModalSessionRef.current += 1;
    setAddConnectorModalOpen(false);
    setAddConnectorStep('pick-scanner');
    setOpenvasConnectorEditId(null);
    const initialZap = getZapConnectorInitialState();
    setZapConnectorDraft(initialZap);
    setZapConnectorSnapshot(initialZap);
    setZapConnectorEditId(null);
    setZapConnectorIsEditing(true);
    setZapConnectorSaveError('');
    setZapConnectorFieldErrors({ name: '', rportClient: '' });
    setZapConnectorSaving(false);
    setShowZapApiKey(false);
    const initialAssignments = cloneZapAssignmentRows();
    setZapAssignmentRows(initialAssignments);
    setZapAssignmentSnapshot(initialAssignments);
    setZapAssignmentSearch('');
  }, []);

  const resetManageUsersInviteDraft = useCallback(() => {
    setManageUsersInviteError('');
    setManageUsersInviteFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
    setManageUsersInviteOrgSearch('');
    setManageUsersInviteSiteSearch('');
    setManageUsersInviteForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'Viewer',
      ...EMPTY_USER_SCOPE_FORM,
    });
  }, []);

  const closeManageUsersInviteModal = useCallback(() => {
    setManageUsersInviteModalOpen(false);
    setManageUsersInviteStep('choice');
    setManageUsersInviteSubmitting(false);
    resetManageUsersInviteDraft();
  }, [resetManageUsersInviteDraft]);

  const openManageUsersInviteModal = useCallback(async () => {
    setManageUsersInviteError('');
    setManageUsersInviteFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
    setManageUsersInviteStep('choice');
    setManageUsersInviteModalOpen(true);
    try {
      const [orgRows, siteRows] = await Promise.all([listOrganizations(), getSites()]);
      const normalized = normalizeUsersOrgsOrganizations(Array.isArray(orgRows) ? orgRows : []);
      setManageUsersInviteOrgs(normalized);
      const normalizedSites = (Array.isArray(siteRows) ? siteRows : [])
        .map((site, idx) => {
          const id = String(site?.uu_id || site?.site_uuid || site?.id || `site-${idx}`).trim();
          const name = String(site?.site_name || site?.name || site?.site || '').trim();
          const orgId = String(
            site?.org_uuid || site?.organization_uuid || site?.organization_uu_id || site?.organization_id || site?.org_id || ''
          ).trim();
          const orgName = String(site?.organization_name || site?.organization || '').trim();
          if (!id || !name) return null;
          return { id, name, orgId, orgName };
        })
        .filter(Boolean);
      setManageUsersInviteSites(normalizedSites);
    } catch {
      setManageUsersInviteOrgs([]);
      setManageUsersInviteSites([]);
      setManageUsersInviteError('Could not load organizations or sites.');
    }
  }, []);

  const submitManageUsersInviteModal = useCallback(async (e) => {
    e.preventDefault();
    const firstName = String(manageUsersInviteForm.firstName || '').trim();
    const lastName = String(manageUsersInviteForm.lastName || '').trim();
    const name = [firstName, lastName].filter(Boolean).join(' ');
    const email = String(manageUsersInviteForm.email || '').trim();
    const role = normalizeUserRoleForForm(manageUsersInviteForm.role);
    const password = String(manageUsersInviteForm.password || '').trim();
    const isAdd = manageUsersInviteStep === 'add';
    const hasAnyText = Boolean(
      firstName || lastName || email || password || String(manageUsersInviteForm.role || '').trim()
    );
    if (!hasAnyText) return;

    const nextFieldErrors = {
      firstName: firstName ? '' : 'Required',
      lastName: lastName ? '' : 'Required',
      email: email ? '' : 'Required',
      password: isAdd && !password ? 'Required' : '',
    };
    if (nextFieldErrors.firstName || nextFieldErrors.lastName || nextFieldErrors.email || nextFieldErrors.password) {
      setManageUsersInviteError('');
      setManageUsersInviteFieldErrors(nextFieldErrors);
      requestAnimationFrame(() => {
        const focusId = !firstName
          ? 'manage-users-add-first-name'
          : !lastName
            ? 'manage-users-add-last-name'
            : !email
              ? 'manage-users-add-email'
              : 'manage-users-add-password';
        const el = document.getElementById(focusId);
        el?.focus?.();
        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
      return;
    }

    if (!hasUserScopeSelection(manageUsersInviteForm)) {
      setManageUsersInviteFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
      setManageUsersInviteError('Select at least one organization, or enable all organizations.');
      return;
    }
    const scopePayload = buildUserScopeCreatePayload(manageUsersInviteForm, manageUsersInviteOrgs);
    if (!scopePayload.organization) {
      setManageUsersInviteFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
      setManageUsersInviteError('Select at least one organization.');
      return;
    }
    setManageUsersInviteSubmitting(true);
    setManageUsersInviteError('');
    setManageUsersInviteFieldErrors({ firstName: '', lastName: '', email: '', password: '' });
    try {
      await createUserAdmin({
        name,
        email,
        action: isAdd ? 'add' : 'invite',
        ...(isAdd && password ? { password } : {}),
        role,
        timezone: getPreferredTimezoneDefault(),
        ...scopePayload,
      });
      closeManageUsersInviteModal();
      // Increment the refresh key so ProfileSettings re-fetches the users list.
      setManageUsersRefreshKey((k) => k + 1);
    } catch (err) {
      setManageUsersInviteError(err?.message || 'Failed to save user.');
    } finally {
      setManageUsersInviteSubmitting(false);
    }
  }, [manageUsersInviteForm, manageUsersInviteStep, manageUsersInviteOrgs, closeManageUsersInviteModal]);

    /**
     * Load real sites from the API and populate the ZAP assignment table.
     * Called when the user navigates to the web-zap step.
     */
  const loadConnectorAssignmentSites = useCallback(async (existingAssignedSiteUuids = [], options = {}) => {
    const {
      setRows,
      setLoading,
      getFallbackRows,
    } = options;
    setLoading(true);
    try {
      const sites = await getSites();
      const siteRows = Array.isArray(sites) ? sites : [];
      const assignedSet = new Set((existingAssignedSiteUuids || []).map((value) => String(value).trim()));
      const nextRows = siteRows.map((site) => {
        const uu = String(site?.uu_id || site?.site_uuid || site?.id || '').trim();
        return {
          id: uu,
          org: String(site?.organization_name || site?.organization || '').trim() || 'Unknown',
          site: String(site?.site_name || site?.name || '').trim() || uu,
          enabled: assignedSet.has(uu),
        };
      }).filter((row) => row.id);
      setRows(nextRows);
      return nextRows;
    } catch {
      const fallback = getFallbackRows();
      setRows(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadZapAssignmentSites = useCallback((existingAssignedSiteUuids = []) => {
    return loadConnectorAssignmentSites(existingAssignedSiteUuids, {
      setRows: setZapAssignmentRows,
      setLoading: setZapSitesLoading,
      getFallbackRows: cloneZapAssignmentRows,
    });
  }, [loadConnectorAssignmentSites]);

  const openNewAddConnectorModal = useCallback((tab = connectorsScannerTab) => {
    const session = (connectorModalSessionRef.current += 1);
    const scannerTab = tab === 'network' ? 'network' : 'web';
    setOpenvasConnectorEditId(null);
    const initialZap = getZapConnectorInitialState();
    setZapConnectorDraft(initialZap);
    setZapConnectorSnapshot(initialZap);
    setZapConnectorEditId(null);
    setZapConnectorIsEditing(true);
    setZapConnectorSaveError('');
    setZapConnectorFieldErrors({ name: '', rportClient: '' });
    setShowZapApiKey(false);
    const initialAssignments = cloneZapAssignmentRows();
    setZapAssignmentRows(initialAssignments);
    setZapAssignmentSnapshot(initialAssignments);
    setZapAssignmentSearch('');
    if (scannerTab === 'network') {
      setAddConnectorStep('network-openvas');
    } else {
      setAddConnectorStep('web-zap');
      loadZapAssignmentSites([]).then((rows) => {
        if (connectorModalSessionRef.current !== session) return;
        setZapAssignmentSnapshot(rows.map((row) => ({ ...row })));
      });
    }
    setAddConnectorModalOpen(true);
  }, [connectorsScannerTab, loadZapAssignmentSites]);

  /**
   * Handle "Configure" click on a connector row.
   * Loads the connector data from the API and opens the edit form.
   */
  const handleConfigureConnector = useCallback(async (connectorId, scannerTab) => {
    const session = (connectorModalSessionRef.current += 1);
    try {
      if (scannerTab === 'web') {
        const data = await getZapConnector(connectorId);
        if (connectorModalSessionRef.current !== session) return;
        const assignedUuids = (data?.enabled_sites || data?.assigned_sites || []).map((site) => String(site?.uu_id || site));
        const nextDraft = {
          name: data?.scanner_name || '',
          rportClient: data?.rport_client_name || '',
          apiKey: '',
          apiKeySet: !!data?.zap_api_key_set,
          port: String(data?.zap_port || ''),
          sshUser: data?.ssh_username || '',
          sshPassword: '',
          sshPasswordSet: !!data?.ssh_password_set,
          poolScope: data?.pool_scope === 'DEDICATED' ? 'DEDICATED' : 'PUBLIC',
          dedicatedOrg: String(data?.dedicated_organization || ''),
          dedicatedOrgName: String(data?.dedicated_organization_name || ''),
          enabled: !!data?.enabled,
        };
        setZapConnectorDraft(nextDraft);
        setZapConnectorSnapshot(nextDraft);
        setZapConnectorEditId(connectorId);
        setZapConnectorIsEditing(true);
        setZapConnectorSaveError('');
        setZapConnectorFieldErrors({ name: '', rportClient: '' });
        setShowZapApiKey(false);
        setZapAssignmentSearch('');
        setAddConnectorStep('web-zap');
        setAddConnectorModalOpen(true);
        const rows = await loadZapAssignmentSites(assignedUuids);
        if (connectorModalSessionRef.current !== session) return;
        setZapAssignmentSnapshot(rows.map((row) => ({ ...row })));
        return;
      }

      if (connectorModalSessionRef.current !== session) return;
      setOpenvasConnectorEditId(connectorId);
      setAddConnectorStep('network-openvas');
      setAddConnectorModalOpen(true);
    } catch (err) {
      if (connectorModalSessionRef.current !== session) return;
      window.alert('Could not load connector: ' + (err?.message || 'Unknown error'));
    }
  }, [loadZapAssignmentSites]);

  /**
   * Handle explicit "Test Connection" action on a connector row.
   * Runs backend test action and refreshes the connector list.
   */
  const handleTestConnector = useCallback(async (connectorId, scannerTab) => {
    try {
      let result;
      if (scannerTab === 'web') result = await testZapConnector(connectorId);
      else result = await testOpenvasConnector(connectorId);

      setConnectorListRefreshKey((k) => k + 1);
      return result;
    } catch (err) {
      if (isPermissionDeniedError(err)) {
        throw new Error('You have read-only connector access. Test connection is not allowed for your role.');
      }
      throw err;
    }
  }, []);

  /**
   * Handle "Delete" click on a connector row.
   * Opens the shared delete confirmation dialog.
   */
  const handleDeleteConnector = useCallback((connectorId, connectorName, scannerTab) => {
    setConnectorDeleteConfirm({
      id: connectorId,
      name: connectorName,
      scannerTab,
    });
  }, []);

  const closeConnectorDeleteConfirm = useCallback(() => {
    if (connectorDeleteBusy) return;
    setConnectorDeleteConfirm(null);
  }, [connectorDeleteBusy]);

  const confirmDeleteConnector = useCallback(async () => {
    if (!connectorDeleteConfirm || connectorDeleteBusy) return;
    const { id: connectorId, scannerTab } = connectorDeleteConfirm;
    setConnectorDeleteBusy(true);
    try {
      if (scannerTab === 'web') await deleteZapConnector(connectorId);
      else await deleteOpenvasConnector(connectorId);
      setConnectorListRefreshKey((k) => k + 1);
      setConnectorDeleteConfirm(null);
    } catch (err) {
      if (isPermissionDeniedError(err)) {
        window.alert('You have read-only connector access. Delete is not allowed for your role.');
        return;
      }
      window.alert('Delete failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setConnectorDeleteBusy(false);
    }
  }, [connectorDeleteBusy, connectorDeleteConfirm]);

  const zapAssignmentPrepared = useMemo(() => {
    const q = zapAssignmentSearch.trim().toLowerCase();
    return [...zapAssignmentRows]
      .filter((r) => !q || r.org.toLowerCase().includes(q) || r.site.toLowerCase().includes(q))
      .sort((a, b) => {
        const orgCmp = a.org.localeCompare(b.org, undefined, { sensitivity: 'base' });
        if (orgCmp !== 0) return orgCmp;
        return a.site.localeCompare(b.site, undefined, { sensitivity: 'base' });
      });
  }, [zapAssignmentRows, zapAssignmentSearch]);

  useEffect(() => {
    if (activePage !== 'settingsConnectors') {
      setAddConnectorModalOpen(false);
    }
  }, [activePage]);

  useEffect(() => {
    if (!newScanModalOpen) {
      setScanManagerFormKey(0);
      prevNewScanStepRef.current = 'pick-type';
    }
  }, [newScanModalOpen]);

  useEffect(() => {
    if (!newScanModalOpen) return;
    const prev = prevNewScanStepRef.current;
    if (
      (newScanStep === 'network-manager' || newScanStep === 'web-manager') &&
      prev === 'pick-type'
    ) {
      setScanManagerFormKey((k) => k + 1);
    }
    prevNewScanStepRef.current = newScanStep;
  }, [newScanModalOpen, newScanStep]);

  const handleWebScanManagerSubmit = useCallback(
    async (payload) => {
      await submitWebApplicationScanSchedule(payload);
      setScansRefreshKey((k) => k + 1);
      navigate(buildScansPathWithFilter({ status: 'all', tab: 'web' }));
      closeNewScanModal();
    },
    [closeNewScanModal, navigate]
  );

  const handleNetworkScanManagerSubmit = useCallback(
    async (payload) => {
      const org = String(payload?.org || selectedOrgId || '').trim();
      const scope = org ? { org_id: org } : null;
      await submitNetworkScanSchedule(payload, null, scope);
      setScansRefreshKey((k) => k + 1);
      navigate(buildScansPathWithFilter({ status: 'all', tab: 'network' }));
      closeNewScanModal();
    },
    [closeNewScanModal, navigate, selectedOrgId]
  );

  const toggleSettingsNav = useCallback(() => {
    setSettingsNavOpen((wasOpen) => {
      const next = !wasOpen;
      if (next) {
        setScansNavOpen(false);
        setXdrNavOpen(false);
      }
      return next;
    });
  }, []);

  const toggleScansNav = useCallback(() => {
    setScansNavOpen((wasOpen) => {
      const next = !wasOpen;
      if (next) {
        setSettingsNavOpen(false);
        setXdrNavOpen(false);
      }
      return next;
    });
  }, []);

  const toggleXdrNav = useCallback(() => {
    setXdrNavOpen((wasOpen) => {
      const next = !wasOpen;
      if (next) {
        setSettingsNavOpen(false);
        setScansNavOpen(false);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${DASHBOARD_MOBILE_NAV_MAX_PX}px)`);
    const sync = () => {
      const narrow = mq.matches;
      setIsNarrowViewport(narrow);
      if (narrow) {
        setSidebarCollapsed(false);
      } else {
        // Leaving drawer mode: restore the persistent desktop sidebar (never leave the floating open tab).
        setSidebarOpen(true);
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!isNarrowViewport || !sidebarOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isNarrowViewport, sidebarOpen]);

  // Basic profile state so header/profile dropdown can reflect updates from Profile Settings.
  const [userProfile, setUserProfile] = useState(buildInitialUserProfile);
  const [profileAccessResolved, setProfileAccessResolved] = useState(() => {
    try {
      const cached = getCachedProfile();
      return Boolean(
        cached?.is_superuser ||
        cached?.role ||
        cached?.capabilities
      );
    } catch {
      return false;
    }
  });
  const handleUserProfileChange = useCallback(
    (updates) => setUserProfile((prev) => ({ ...prev, ...(updates || {}) })),
    []
  );

  const uiAccess = useMemo(() => buildUiAccessPolicy(userProfile), [userProfile]);

  // Only global admins choose whether a web scanner is public or dedicated.
  useEffect(() => {
    if (!addConnectorModalOpen || !uiAccess.isSuperuser) return undefined;
    let mounted = true;
    listOrganizations()
      .then((rows) => {
        if (!mounted) return;
        setZapPoolOrgOptions(
          (Array.isArray(rows) ? rows : [])
            .map((org) => ({ value: String(org?.uu_id || '').trim(), label: String(org?.name || '').trim() }))
            .filter((org) => org.value && org.label)
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => {
        if (mounted) setZapPoolOrgOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, [addConnectorModalOpen, uiAccess.isSuperuser]);
  const canEditSiteOrganization = uiAccess.isSuperuser || (uiAccess.role === 'admin' && String(userProfile?.organization_id || '').trim() === '1');

  useEffect(() => {
    setEditSiteCanChangeOrg(canEditSiteOrganization);
  }, [canEditSiteOrganization]);

  const visibleSettingsNavItems = useMemo(
    () => APP_SETTINGS_NAV.filter((item) => {
      if (item.page === 'settingsConnectors') return uiAccess.canSeeSettingsConnectors;
      if (item.page === 'settingsDeployment') return uiAccess.canSeeSettingsDeployment;
      if (item.page === 'settingsPartners') return uiAccess.canSeeSettingsPartners;
      if (item.page === 'settingsManageUsers') return uiAccess.canSeeSettingsManageUsers;
      if (item.page === 'settingsUsersOrgs') return uiAccess.canSeeSettingsUsersOrgs;
      if (item.page === 'settingsSites') return uiAccess.canSeeSettingsUsersOrgs;
      if (item.page === 'settingsScanNetwork') return uiAccess.canSeeSettingsScanNetwork;
      if (item.page === 'settingsScanNetworkOps') return uiAccess.canSeeSettingsScanNetworkOps;
      return false;
    }),
    [uiAccess]
  );

  const defaultSettingsPage = useMemo(
    () => (visibleSettingsNavItems[0]?.page || 'profileSettings'),
    [visibleSettingsNavItems]
  );

  const canAccessPage = useCallback(
    (page) => {
      const p = normalizePageKey(page);
      if (p === 'subscription' || p === 'subscriptionOrgDetail') return uiAccess.canSeeSubscription;
      if (p === 'settingsConnectors') return uiAccess.canSeeSettingsConnectors;
      if (p === 'settingsDeployment') return uiAccess.canSeeSettingsDeployment;
      if (p === 'settingsPartners') return uiAccess.canSeeSettingsPartners;
      if (p === 'settingsManageUsers') return uiAccess.canSeeSettingsManageUsers;
      if (p === 'settingsUsersOrgs') return uiAccess.canSeeSettingsUsersOrgs;
      if (p === 'settingsSites') return uiAccess.canSeeSettingsUsersOrgs;
      if (p === 'settingsScanNetwork') return uiAccess.canSeeSettingsScanNetwork;
      if (p === 'settingsScanNetworkOps') return uiAccess.canSeeSettingsScanNetworkOps;
      return true;
    },
    [uiAccess]
  );

  const handleSelectPage = (page) => {
    if (
      page !== 'scansWeb'
      && page !== 'scansNetwork'
      && page !== 'scansPerimeter'
      && !canAccessPage(page)
    ) return;

    const nextPage =
      page === 'settingsConnectors' && !uiAccess.canSeeSettingsConnectors
        ? defaultSettingsPage
        : page === 'scansWeb' || page === 'scansNetwork' || page === 'scansPerimeter' || page === 'scans'
          ? 'scans'
          : page;

    if (page === 'earlyDisclosures2') {
      setGhostExposuresFilterSeed((s) => ({ nonce: s.nonce + 1, cveQuery: '' }));
    }
    if (APP_SETTINGS_PAGES.includes(nextPage)) {
      setSettingsNavOpen(true);
    } else {
      setSettingsNavOpen(false);
    }
    if (nextPage === 'scans') {
      setScansNavOpen(true);
    } else if (nextPage !== 'scanDetail') {
      setScansNavOpen(false);
    }
    if (isXdrNavPage(nextPage)) {
      setXdrNavOpen(true);
    } else {
      setXdrNavOpen(false);
    }
    if (page === 'scansWeb') {
      setScansInitialTab('web');
      navigate(buildScansPathWithFilter({ status: 'all', tab: 'web' }));
    } else if (page === 'scansNetwork') {
      setScansInitialTab('network');
      navigate(buildScansPathWithFilter({ status: 'all', tab: 'network', location: 'onprem' }));
    } else if (page === 'scansPerimeter') {
      setScansInitialTab('network');
      navigate(buildScansPathWithFilter({ status: 'all', tab: 'network', location: 'perimeter' }));
    } else if (nextPage === 'scans') {
      setScansInitialTab('web');
      navigate(buildScansPathWithFilter({ status: 'all', tab: 'web' }));
    } else if (nextPage === 'risks') {
      navigate(buildRisksPathWithFilters({ status: 'open' }));
    } else {
      navigateToPage(nextPage);
    }
    if (typeof window !== 'undefined' && window.matchMedia(`(max-width: ${DASHBOARD_MOBILE_NAV_MAX_PX}px)`).matches) {
      setSidebarOpen(false);
    }
  };

  // Load inventory data when the user opens the Assets page.
  useEffect(() => {
    if (activePage !== 'targets') return;

    let isMounted = true;
    // Bump when the Assets data composition changes so Fast Refresh cannot
    // preserve an obsolete cached inventory result.
    const scopeKey = `assets-v4:${JSON.stringify(dashboardScopeQuery || {})}`;
    const scopeChanged = assetsInventoryScopeKeyRef.current !== scopeKey;
    const keepStaleWhileRefresh =
      !scopeChanged && Array.isArray(assetsInventoryRef.current);

    if (
      keepStaleWhileRefresh &&
      Date.now() - assetsFetchedAtRef.current < 3 * 60 * 1000
    ) {
      return undefined;
    }

    if (scopeChanged) {
      assetsInventoryScopeKeyRef.current = scopeKey;
      // Only wipe on org/site scope change — keep cached rows when revisiting Assets.
      setAssetsInventory(null);
    }

    setAssetsLoading(true);

    getInventory(null, dashboardScopeQuery, {
      // Progressive paint on cold load only; background refresh waits for full result.
      onPartial: keepStaleWhileRefresh
        ? null
        : (partialRows) => {
            if (!isMounted) return;
            setAssetsInventory(Array.isArray(partialRows) ? partialRows : []);
          },
    })
      .then((data) => {
        if (!isMounted || assetsInventoryScopeKeyRef.current !== scopeKey) return;
        setAssetsInventory(Array.isArray(data) ? data : []);
        assetsFetchedAtRef.current = Date.now();
        setAssetsDataWarning('');
      })
      .catch((err) => {
        if (!isMounted) return;
        // Keep any partial/cached rows already shown unless we never received data.
        setAssetsInventory((prev) => (Array.isArray(prev) ? prev : []));
        setAssetsDataWarning(
          `Assets unavailable: ${toUserFacingErrorMessage(err?.message, 'request failed')}`
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setAssetsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activePage, dashboardScopeQuery]);
  const {
    showNotifications, setShowNotifications,
    notifications, notificationsLoading, notificationsError,
    unreadCount, loadNotifications, handleNotificationClick,
  } = useNotifications({ navigate });
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showTourMenu, setShowTourMenu] = useState(false);

  useEffect(() => {
    let isMounted = true;
    getProfile()
      .then((data) => {
        if (!isMounted) return;
        setUserProfile((prev) => mapApiProfileToUserProfile(data, prev));
      })
      .catch(() => {
        // Keep existing fallback values when profile API is unavailable.
      })
      .finally(() => {
        if (isMounted) setProfileAccessResolved(true);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!profileAccessResolved) return;
    if (!canAccessPage(activePage)) {
      navigateToPage('dashboard', { replace: true });
    }
  }, [activePage, canAccessPage, navigateToPage, profileAccessResolved]);

  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const tourMenuRef = useRef(null);
  const universalSearchOuterRef = useRef(null);
  const orgDropdownRef = useRef(null);
  const siteDropdownRef = useRef(null);
  const overviewDateRangeRef = useRef(null);


  const createSiteDialogTitleId = useId();
  const createSiteNameFieldId = useId();
  const createSiteDescFieldId = useId();
  const createSiteOrgFieldId = useId();
  const editSiteDialogTitleId = useId();
  const editSiteNameFieldId = useId();
  const editSiteDescFieldId = useId();
  const editSiteDetailsFieldId = useId();
  const editSiteStatusFieldId = useId();
  const editSiteOrgFieldId = useId();
  const editSiteActiveFieldId = useId();

  const closeCreateSiteModal = useCallback(() => {
    setCreateSiteModalOpen(false);
    setCreateSiteModalError('');
    setCreateSiteSubmitting(false);
  }, []);

  const closeEditSiteModal = useCallback(() => {
    setEditSiteModalOpen(false);
    setEditSiteSubmitting(false);
    setEditSiteModalError('');
  }, []);

  const openCreateSiteModal = useCallback(() => {
    setCreateSiteModalError('');
    setCreateSiteName('');
    setCreateSiteDescription('');
    const fromHeader = String(selectedOrgId || '').trim();
    const firstOrg = orgs.length ? getOrgIdentity(orgs[0]) : '';
    setCreateSiteOrganizationId(fromHeader || firstOrg);
    setCreateSiteModalOpen(true);
  }, [orgs, selectedOrgId, getOrgIdentity]);

  const openEditSiteModal = useCallback(async () => {
    const siteId = String(selectedSiteId || '').trim();
    if (!siteId) return;
    const selected = headerSites.find((row) => String(row?.id || '').trim() === siteId);

    let role = 'viewer';
    let orgId = '';
    try {
      const cachedProfile = getCachedProfile() || {};
      role = normalizeUiRole(cachedProfile);
      orgId = String(cachedProfile?.organization_id || '').trim();
    } catch {
      role = 'viewer';
      orgId = '';
    }
    const canChangeOrg = role === 'superuser' || (role === 'admin' && orgId === '1');

    setEditSiteCanChangeOrg(canChangeOrg);
    setEditSiteModalError('');
    setEditSiteSubmitting(true);
    setEditSiteModalOpen(true);

    try {
      // Always hydrate the edit form from the latest backend record to avoid stale values.
      const latest = await getSites(null, { site_uu_id: siteId });
      const site = Array.isArray(latest) ? latest[0] : latest;

      const fetchedOrgId = String(
        site?.organization_id || site?.org_id || site?.organization_uuid || site?.org_uuid || ''
      ).trim();
      const fallbackOrgId = String(selected?.orgId || selectedOrgId || '').trim();

      setEditSiteForm({
        uuId: siteId,
        organizationId: fetchedOrgId || fallbackOrgId,
        siteName: String(site?.site_name || site?.name || selected?.name || '').trim(),
        siteDescription: String(site?.site_disc || site?.description || selected?.description || '').trim(),
        siteDetails: String(site?.site_owner || site?.site_details || selected?.details || '').trim(),
        siteStatus: String(site?.site_status || selected?.status || '').trim(),
        isActive:
          typeof site?.is_active === 'boolean'
            ? site.is_active
            : Boolean(selected?.isActive),
      });
    } catch (err) {
      if (!selected) {
        setEditSiteModalError(err?.message || 'Could not load current site details.');
        return;
      }

      // Fallback to locally cached selector data if backend fetch fails.
      setEditSiteModalError('Could not refresh latest details. Showing last known values.');
      setEditSiteForm({
        uuId: siteId,
        organizationId: String(selected?.orgId || selectedOrgId || '').trim(),
        siteName: String(selected?.name || '').trim(),
        siteDescription: String(selected?.description || '').trim(),
        siteDetails: String(selected?.details || '').trim(),
        siteStatus: String(selected?.status || '').trim(),
        isActive: Boolean(selected?.isActive),
      });
    } finally {
      setEditSiteSubmitting(false);
    }
  }, [headerSites, selectedOrgId, selectedSiteId]);

  const submitCreateSiteModal = useCallback(
    async (e) => {
      e.preventDefault();
      const name = String(createSiteName || '').trim();
      const orgUuid = String(createSiteOrganizationId || '').trim();
      const desc = String(createSiteDescription || '').trim();
      if (!name) {
        setCreateSiteModalError('Enter a site name.');
        return;
      }
      if (!orgUuid) {
        setCreateSiteModalError('Select an organization.');
        return;
      }
      setCreateSiteSubmitting(true);
      setCreateSiteModalError('');
      try {
        await createSite({
          site_name: name,
          site_disc: desc,
          organization_uuid: orgUuid,
        });
        closeCreateSiteModal();
        void loadHeaderSites();
      } catch (err) {
        setCreateSiteModalError(err?.message || 'Failed to create site.');
      } finally {
        setCreateSiteSubmitting(false);
      }
    },
    [createSiteName, createSiteDescription, createSiteOrganizationId, closeCreateSiteModal, loadHeaderSites]
  );

  const submitEditSiteModal = useCallback(
    async (e) => {
      e.preventDefault();
      const siteName = String(editSiteForm.siteName || '').trim();
      if (!siteName) {
        setEditSiteModalError('Enter a site name.');
        return;
      }

      setEditSiteSubmitting(true);
      setEditSiteModalError('');
      try {
        const payload = {
          uu_id: editSiteForm.uuId,
          site_name: siteName,
          site_disc: String(editSiteForm.siteDescription || '').trim(),
          site_owner: String(editSiteForm.siteDetails || '').trim(),
          site_status: String(editSiteForm.siteStatus || '').trim(),
          is_active: Boolean(editSiteForm.isActive),
        };
        if (editSiteCanChangeOrg) {
          payload.organization_uuid = String(editSiteForm.organizationId || '').trim();
        }

        await updateSite(payload);
        closeEditSiteModal();
        void loadHeaderSites();
      } catch (err) {
        setEditSiteModalError(err?.message || 'Failed to update site.');
      } finally {
        setEditSiteSubmitting(false);
      }
    },
    [editSiteForm, editSiteCanChangeOrg, closeEditSiteModal, loadHeaderSites]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target?.closest?.('#driver-popover-content, .driver-overlay, .driver-active-element')) {
        return;
      }
      const insideNotifications = notificationsRef.current?.contains(event.target);
      const insideProfile = profileRef.current?.contains(event.target);
      const insideTourMenu = tourMenuRef.current?.contains(event.target);
      const insideOrgDropdown = orgDropdownRef.current?.contains(event.target);
      const insideSiteDropdown = siteDropdownRef.current?.contains(event.target);
      const insideOverviewDateRange = overviewDateRangeRef.current?.contains(event.target);
      if (!insideNotifications && !insideProfile) {
        setShowNotifications(false);
        setShowProfileMenu(false);
        setShowTourMenu(false);
      } else if (!insideTourMenu) {
        setShowTourMenu(false);
      }
      if (!insideOrgDropdown) {
        setOrgDropdownOpen(false);
      }
      if (!insideSiteDropdown) {
        setSiteDropdownOpen(false);
      }
      if (!insideOverviewDateRange) {
        setOverviewDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const closeConnectorConfig = () => {
      if (addConnectorModalOpen) closeAddConnectorModal();
    };
    const closeSubscriptionOrg = () => {
      if (activePage === 'subscriptionOrgDetail') handleSelectPage('subscription');
    };
    const openNewScan = () => {
      setNewScanSkipPickType(false);
      setNewScanStep('pick-type');
      setNewScanModalOpen(true);
    };
    const closeNewScan = () => {
      if (newScanModalOpen) closeNewScanModal();
    };
    window.addEventListener('xspectre:tour-close-connector-config', closeConnectorConfig);
    window.addEventListener('xspectre:tour-close-subscription-org', closeSubscriptionOrg);
    const openProfileMenu = () => {
      setShowNotifications(false);
      setShowTourMenu(false);
      setShowProfileMenu(true);
    };
    const closeProfileMenu = () => {
      setShowProfileMenu(false);
      setShowTourMenu(false);
    };
    window.addEventListener('xspectre:tour-open-new-scan', openNewScan);
    window.addEventListener('xspectre:tour-close-new-scan', closeNewScan);
    window.addEventListener('xspectre:tour-open-profile-menu', openProfileMenu);
    window.addEventListener('xspectre:tour-close-profile-menu', closeProfileMenu);
    return () => {
      window.removeEventListener('xspectre:tour-close-connector-config', closeConnectorConfig);
      window.removeEventListener('xspectre:tour-close-subscription-org', closeSubscriptionOrg);
      window.removeEventListener('xspectre:tour-open-new-scan', openNewScan);
      window.removeEventListener('xspectre:tour-close-new-scan', closeNewScan);
      window.removeEventListener('xspectre:tour-open-profile-menu', openProfileMenu);
      window.removeEventListener('xspectre:tour-close-profile-menu', closeProfileMenu);
    };
  }, [activePage, addConnectorModalOpen, closeAddConnectorModal, closeNewScanModal, handleSelectPage, newScanModalOpen]);

  useEffect(() => {
    if (!newScanModalOpen && !addConnectorModalOpen && !createSiteModalOpen && !editSiteModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (editSiteModalOpen) {
        closeEditSiteModal();
        return;
      }
      if (createSiteModalOpen) {
        closeCreateSiteModal();
        return;
      }
      if (addConnectorModalOpen) {
        if (addConnectorStep === 'network-openvas') {
          closeAddConnectorModal();
        } else if (addConnectorStep === 'web-zap') {
          setShowZapApiKey(false);
          setAddConnectorStep('pick-scanner');
        } else {
          closeAddConnectorModal();
        }
        return;
      }
      // First Escape from a manager returns to scan type (unless opened from Web/Network scans);
      // from pick-type it closes the overlay.
      if (newScanStep === 'network-manager' || newScanStep === 'web-manager') {
        if (newScanSkipPickType) closeNewScanModal();
        else setNewScanStep('pick-type');
      } else {
        closeNewScanModal();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    addConnectorModalOpen,
    addConnectorStep,
    createSiteModalOpen,
    editSiteModalOpen,
    closeEditSiteModal,
    closeCreateSiteModal,
    newScanModalOpen,
    newScanStep,
    newScanSkipPickType,
    closeNewScanModal,
    closeAddConnectorModal,
  ]);

  const connectorsOverviewHintId = useId();
  const universalSearchFieldId = useId();
  const universalSearchResultsRegionId = useId();

  const universalSearchPanelModel = useMemo(() => {
    const q = universalSearchQuery.trim();
    const hasTypedQuery = q.length > 0;
    if (!hasTypedQuery) {
      return { hasTypedQuery: false, assets: [], vulns: [], connectors: [], totalMatches: 0 };
    }

    const assetRows = buildAssetTableRows(assetsInventory);
    const assets = assetRows
      .filter((r) =>
        universalSearchTextMatches(
          [
            r.hostname,
            r.ipAddress,
            r.site,
            r.type,
            r.macAddress,
            r.os,
            r.software,
            r.manufacturer,
            String(r.certificates),
            String(r.vulnerabilities),
            r.lastScan,
            r.openPorts,
          ],
          q
        )
      )
      .slice(0, UNIVERSAL_SEARCH_RESULTS_CAP)
      .map((r, idx) => ({
        key: `asset-${r.assetId || r.hostname || r.ipAddress || `i${idx}`}`,
        title: r.hostname || r.ipAddress || 'Asset',
        subtitle: [r.ipAddress, r.site, r.type].filter(Boolean).join('  '),
      }));

    const vulnRows = buildDashboardVulnRows(vulnerabilities);
    const vulns = vulnRows
      .filter((row) => universalSearchTextMatches([row.title, row.severity, String(row.count)], q))
      .slice(0, UNIVERSAL_SEARCH_RESULTS_CAP)
      .map((row, idx) => ({
        key: `vuln-${idx}-${String(row.title).slice(0, 48)}`,
        title: row.title,
        subtitle: `Severity ${row.severity}  Count ${row.count}`,
      }));

    const connectors = [];

    const totalMatches = assets.length + vulns.length + connectors.length;
    return { hasTypedQuery: true, assets, vulns, connectors, totalMatches };
  }, [universalSearchQuery, assetsInventory, vulnerabilities]);

  useEffect(() => {
    if (!universalSearchDropdownOpen) return undefined;
    const onDocMouseDown = (event) => {
      if (universalSearchOuterRef.current && !universalSearchOuterRef.current.contains(event.target)) {
        setUniversalSearchDropdownOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setUniversalSearchDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [universalSearchDropdownOpen]);

  const showUniversalSearchPanel = universalSearchDropdownOpen;
  const dashboardTourIdentity =
    userProfile?.uu_id || userProfile?.email || userProfile?.name || 'browser-user';
  const prepareDashboardTour = useCallback(() => {
    const previousSidebarOpen = sidebarOpen;
    const previousSidebarCollapsed = sidebarCollapsed;
    const previousScansNavOpen = scansNavOpen;
    const previousSettingsNavOpen = settingsNavOpen;

    setUniversalSearchDropdownOpen(false);
    setOrgDropdownOpen(false);
    setSiteDropdownOpen(false);
    setShowNotifications(false);
    setShowProfileMenu(false);
    setShowTourMenu(false);
    setScansNavOpen(true);
    setSettingsNavOpen(true);
    if (isNarrowViewport) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
      setSidebarCollapsed(false);
    }

    return () => {
      setSidebarOpen(previousSidebarOpen);
      setSidebarCollapsed(previousSidebarCollapsed);
      setScansNavOpen(previousScansNavOpen);
      setSettingsNavOpen(previousSettingsNavOpen);
    };
  }, [
    isNarrowViewport,
    scansNavOpen,
    settingsNavOpen,
    setOrgDropdownOpen,
    setShowNotifications,
    setSiteDropdownOpen,
    setUniversalSearchDropdownOpen,
    sidebarCollapsed,
    sidebarOpen,
  ]);
  const [profileSettingsTab, setProfileSettingsTab] = useState(null);
  const { startTourFrom, tourStartOptions } = useDashboardTour({
    activePage,
    identity: dashboardTourIdentity,
    isReady:
      DASHBOARD_TOURS_ENABLED &&
      profileAccessResolved &&
      !newScanModalOpen &&
      !addConnectorModalOpen &&
      !createSiteModalOpen &&
      !editSiteModalOpen &&
      !manageUsersInviteModalOpen,
    isNarrowViewport,
    scansPageTab,
    onNavigateToDashboard: () => handleSelectPage('dashboard'),
    onNavigateToPage: handleSelectPage,
    onPrepare: prepareDashboardTour,
  });
  const {
    open: mfaEnrollmentOpen,
  } = useMfaEnrollmentPrompt({
    identity: dashboardTourIdentity,
    activePage,
  });

  const PAGE_TITLES = {
    dashboard: 'Security Overview',
    targets: 'Assets',
    scans: scansPageTab === 'network'
      ? (scansFilterSeed.location === 'perimeter' ? 'Perimeter Scans' : 'On-premise Scans')
      : 'Web Scans',
    activeScans: 'Activity Log',
    ocva: 'OCVA Assessments',
    earlyDisclosures: 'Early Disclosure Intelligence',
    earlyDisclosures2: 'Ghost CVE Exposures',
    risks: 'Vulnerabilities',
    riskDetail: 'Vulnerability Detail',
    scanDetail: 'Scan Detail',
    reports: 'Reports',
    aiIntelligence: 'xSPECTRE Intelligence',
    securityEvents: 'Security Events',
    alerts: 'Alerts',
    xdr: 'XDR Integrations',
    subscription: 'Subscription',
    subscriptionOrgDetail: 'Plan & Usage',
    support: 'Support',
    profileSettings: 'Profile Settings',
    settingsConnectors: 'Connectors',
    settingsDeployment: 'Deployment',
    settingsUsersOrgs: 'Manage Organizations',
    settingsSites: 'Manage Sites',
    settingsScanNetwork: 'Scan Network',
    settingsScanNetworkOps: 'Scan Network Ops',
    settingsManageUsers: 'Manage Users',
    settingsPartners: 'Partners',
  };

  useEffect(() => {
    // Persist last selected page so refresh returns to it.
    try {
      if (activePage) localStorage.setItem('xspectre_active_page', activePage);
    } catch (_) { /* ignore */ }
  }, [activePage]);

  const chromeMenuOpen =
    orgDropdownOpen
    || siteDropdownOpen
    || showNotifications
    || showProfileMenu
    || showUniversalSearchPanel;

  return (
    <div
      className={`dashboard-container ${!isDarkMode ? 'theme-light' : ''}${
        isNarrowViewport ? ' dashboard-container--narrow' : ''
      }${chromeMenuOpen ? ' chrome-menu-open' : ''}`}
    >
      <div className="dashboard-body">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          showMobileClose={isNarrowViewport}
          isCollapsed={isNarrowViewport ? false : sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
          activePage={activePage}
          activeScanTab={
            activePage === 'scanDetail'
              ? (scanDetailType === 'network' ? 'network' : 'web')
              : (scansPageTab === 'network' ? 'network' : 'web')
          }
          activeScanLocation={
            (() => {
              if (activePage === 'scanDetail') {
                const fromState = String(location.state?.scanLocation || '').trim().toLowerCase();
                if (fromState === 'perimeter' || fromState === 'onprem') return fromState;
              }
              return scansFilterSeed.location === 'perimeter' ? 'perimeter' : 'onprem';
            })()
          }
          onSelectPage={handleSelectPage}
          settingsNavOpen={settingsNavOpen}
          onToggleSettingsNav={toggleSettingsNav}
          settingsNavItems={visibleSettingsNavItems}
          defaultSettingsPage={defaultSettingsPage}
          showSubscription={uiAccess.canSeeSubscription}
          scansNavOpen={scansNavOpen}
          onToggleScansNav={toggleScansNav}
          xdrNavOpen={xdrNavOpen}
          onToggleXdrNav={toggleXdrNav}
        />
        {isNarrowViewport && sidebarOpen ? (
          <div
            className="sidebar-mobile-backdrop"
            role="presentation"
            onClick={() => setSidebarOpen(false)}
          />
        ) : null}
        <div className="main-content">
          {/* Universal search: filters in-memory tables on the current app surfaces (see `universalSearchQuery` state). */}
          <header className={`main-header${chromeMenuOpen ? ' main-header--menu-open' : ''}`}>
            {isNarrowViewport && !sidebarOpen ? (
              <button
                type="button"
                className="sidebar-open-btn"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                title="Open menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
                <span className="sidebar-open-btn-label">Open menu</span>
              </button>
            ) : null}
            <div
              className="main-header-search-slot"
              ref={universalSearchOuterRef}
              data-tour="universal-search"
            >
              <div className="main-header-search-wrap" role="search">
                <label htmlFor={universalSearchFieldId} className="edi-sr-only">
                  Universal search  filters assets, scans, connectors, and open vulnerabilities; quick results below
                  while typing
                </label>
                <span className="main-header-search-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  id={universalSearchFieldId}
                  type="search"
                  className="main-header-search"
                  placeholder="Search assets, scans, connectors, vulnerabilities"
                  aria-label="Universal search"
                  aria-controls={showUniversalSearchPanel ? universalSearchResultsRegionId : undefined}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={UNIVERSAL_SEARCH_MAX_LEN}
                  value={universalSearchQuery}
                  onFocus={() => setUniversalSearchDropdownOpen(true)}
                  onChange={(e) => {
                    setUniversalSearchQuery(sanitizeUniversalSearchInput(e.target.value));
                    setUniversalSearchDropdownOpen(true);
                  }}
                />
              </div>
              {showUniversalSearchPanel && (
                <div
                  className="universal-search-results"
                  id={universalSearchResultsRegionId}
                  role="region"
                  aria-label="Universal search quick results"
                >
                  {!universalSearchPanelModel.hasTypedQuery ? (
                    <p className="universal-search-results-hint">
                      Type to see quick matches (up to {UNIVERSAL_SEARCH_RESULTS_CAP} per category). Choose a row to
                      jump to that area; the same search text still filters tables on each page.
                    </p>
                  ) : universalSearchPanelModel.totalMatches === 0 ? (
                    <>
                      <p className="universal-search-results-empty">
                        No matches in assets, open vulnerabilities, or connectors for this search.
                      </p>
                      <button
                        type="button"
                        className="universal-search-result-row universal-search-result-row--muted"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          handleSelectPage('scans');
                          setUniversalSearchDropdownOpen(false);
                        }}
                      >
                        <span className="universal-search-result-title">Scans</span>
                        <span className="universal-search-result-sub">
                          Web and network history on the Scans page uses this same search
                        </span>
                      </button>
                    </>
                  ) : (
                    <>
                      {universalSearchPanelModel.assets.length > 0 ? (
                        <div className="universal-search-results-section">
                          <div className="universal-search-results-heading" id={`${universalSearchResultsRegionId}-h-a`}>
                            Assets
                          </div>
                          <ul
                            className="universal-search-results-ul"
                            aria-labelledby={`${universalSearchResultsRegionId}-h-a`}
                          >
                            {universalSearchPanelModel.assets.map((item) => (
                              <li key={item.key}>
                                <button
                                  type="button"
                                  className="universal-search-result-row"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    handleSelectPage('targets');
                                    setUniversalSearchDropdownOpen(false);
                                  }}
                                >
                                  <span className="universal-search-result-title">{item.title}</span>
                                  {item.subtitle ? (
                                    <span className="universal-search-result-sub">{item.subtitle}</span>
                                  ) : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {universalSearchPanelModel.vulns.length > 0 ? (
                        <div className="universal-search-results-section">
                          <div className="universal-search-results-heading" id={`${universalSearchResultsRegionId}-h-v`}>
                            Open vulnerabilities
                          </div>
                          <ul
                            className="universal-search-results-ul"
                            aria-labelledby={`${universalSearchResultsRegionId}-h-v`}
                          >
                            {universalSearchPanelModel.vulns.map((item) => (
                              <li key={item.key}>
                                <button
                                  type="button"
                                  className="universal-search-result-row"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    handleSelectPage('dashboard');
                                    setUniversalSearchDropdownOpen(false);
                                  }}
                                >
                                  <span className="universal-search-result-title">{item.title}</span>
                                  {item.subtitle ? (
                                    <span className="universal-search-result-sub">{item.subtitle}</span>
                                  ) : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {universalSearchPanelModel.connectors.length > 0 ? (
                        <div className="universal-search-results-section">
                          <div className="universal-search-results-heading" id={`${universalSearchResultsRegionId}-h-c`}>
                            Connectors
                          </div>
                          <ul
                            className="universal-search-results-ul"
                            aria-labelledby={`${universalSearchResultsRegionId}-h-c`}
                          >
                            {universalSearchPanelModel.connectors.map((item) => (
                              <li key={item.key}>
                                <button
                                  type="button"
                                  className="universal-search-result-row"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    handleSelectPage('settingsConnectors');
                                    setUniversalSearchDropdownOpen(false);
                                  }}
                                >
                                  <span className="universal-search-result-title">{item.title}</span>
                                  {item.subtitle ? (
                                    <span className="universal-search-result-sub">{item.subtitle}</span>
                                  ) : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      <div className="universal-search-results-section universal-search-results-section--footer">
                        <button
                          type="button"
                          className="universal-search-result-row universal-search-result-row--muted"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            handleSelectPage('scans');
                            setUniversalSearchDropdownOpen(false);
                          }}
                        >
                          <span className="universal-search-result-title">Scans</span>
                          <span className="universal-search-result-sub">
                            Tables on the Scans page filter with this same search
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="main-header-icons" data-tour="scope-controls">
              {/* Org selector */}
              {orgs.length > 1 ? (
              <div className="org-selector" ref={orgDropdownRef}>
                <button
                  type="button"
                  className={`org-selector-btn${selectedOrgId ? ' is-active' : ''}`}
                  title="Select organization"
                  onClick={() => {
                    setOrgDropdownOpen((o) => !o);
                    setOrgSearch('');
                    setSiteDropdownOpen(false);
                    setOverviewDateRangeOpen(false);
                  }}
                >
                  <span className="org-selector-label">
                    {selectedOrgId
                      ? (orgs.find((o) => getOrgIdentity(o) === selectedOrgId)?.name ?? 'Organization')
                      : 'All Organizations'}
                  </span>
                  {selectedOrgId ? (
                    <span
                      className="org-selector-clear"
                      role="button"
                      tabIndex={0}
                      aria-label="Clear organization"
                      title="Clear organization"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedOrgId('');
                        localStorage.removeItem('xspectre_selected_org_id');
                        setSelectedSiteId('');
                        localStorage.removeItem('xspectre_selected_site_id');
                        setOrgDropdownOpen(false);
                        setSiteDropdownOpen(false);
                        setOrgSearch('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedOrgId('');
                        localStorage.removeItem('xspectre_selected_org_id');
                        setSelectedSiteId('');
                        localStorage.removeItem('xspectre_selected_site_id');
                        setOrgDropdownOpen(false);
                        setSiteDropdownOpen(false);
                        setOrgSearch('');
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                  ) : (
                    <svg className="org-selector-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                  )}
                </button>
                {orgDropdownOpen && (
                  <div className="org-selector-dropdown">
                    {orgsError && <div className="org-selector-meta org-selector-meta-error">{orgsError}</div>}
                    {!orgsError && orgsHint && (
                      <div className="org-selector-meta org-selector-meta-hint">{orgsHint}</div>
                    )}
                    <div className="org-selector-search-wrap">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                      <input
                        type="text"
                        className="org-selector-search"
                        placeholder="Search organization"
                        value={orgSearch}
                        autoFocus
                        onChange={(e) => setOrgSearch(e.target.value)}
                      />
                    </div>
                    <ul className="org-selector-list">
                      {orgsLoading && (
                        <li className="org-selector-empty">
                          Loading organizations…
                        </li>
                      )}
                      <li
                        className={`org-selector-item org-selector-item--split${!selectedOrgId ? ' is-selected' : ''}`}
                        onClick={() => {
                          setSelectedOrgId('');
                          localStorage.removeItem('xspectre_selected_org_id');
                          setSelectedSiteId('');
                          localStorage.removeItem('xspectre_selected_site_id');
                          setOrgDropdownOpen(false);
                          setOrgSearch('');
                        }}
                      >
                        <span className="org-selector-item-label">All Organizations</span>
                        <span className="org-selector-item-metric" aria-label={`${orgs.length} organizations`}>
                          {orgs.length}
                        </span>
                      </li>
                      {filteredHeaderOrgs
                        .map((o, idx) => (
                          <li
                            key={`${getOrgIdentity(o) || 'org'}-${idx}`}
                            className={`org-selector-item${getOrgIdentity(o) === selectedOrgId ? ' is-selected' : ''}`}
                            onClick={() => handleOrgSelect(o)}
                          >
                            {String(o?.name || '').trim() || String(o?.uu_id || '').trim() || `Organization ${idx + 1}`}
                          </li>
                        ))
                      }
                      {filteredHeaderOrgs.length === 0 && (
                        <li className="org-selector-empty">No organizations found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              ) : null}
              {/* Site selector (scopes dashboard APIs with org_id + site_uuid when supported). */}
              <div className="org-selector" ref={siteDropdownRef}>
                <button
                  type="button"
                  className={`org-selector-btn${selectedSiteId ? ' is-active' : ''}`}
                  title="Select site"
                  onClick={() => {
                    setSiteDropdownOpen((o) => !o);
                    setSiteSearch('');
                    setOrgDropdownOpen(false);
                    setOverviewDateRangeOpen(false);
                  }}
                >
                  <span className="org-selector-label">
                    {selectedSiteId
                      ? (headerSites.find((s) => s.id === selectedSiteId)?.name ?? 'Site')
                      : 'All Sites'}
                  </span>
                  {selectedSiteId ? (
                    <span
                      className="org-selector-clear"
                      role="button"
                      tabIndex={0}
                      aria-label="Clear site"
                      title="Clear site"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSiteSelect(null);
                        setSiteDropdownOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        handleSiteSelect(null);
                        setSiteDropdownOpen(false);
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                  ) : (
                    <svg
                      className="org-selector-chevron"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  )}
                </button>
                {siteDropdownOpen && (
                  <div className="org-selector-dropdown">
                    <button
                      type="button"
                      className="org-selector-create-site-btn"
                      onClick={() => {
                        setSiteDropdownOpen(false);
                        setSiteSearch('');
                        openCreateSiteModal();
                      }}
                    >
                      + Add New Site
                    </button>
                    <div className="org-selector-search-wrap">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        className="org-selector-search"
                        placeholder="Search site"
                        value={siteSearch}
                        autoFocus
                        onChange={(e) => setSiteSearch(e.target.value)}
                      />
                    </div>
                    <ul className="org-selector-list">
                      {headerSitesLoading && (
                        <li className="org-selector-empty">
                          Loading sites…
                        </li>
                      )}
                      <li
                        className={`org-selector-item org-selector-item--split${!selectedSiteId ? ' is-selected' : ''}`}
                        onClick={() => handleSiteSelect(null)}
                      >
                        <span className="org-selector-item-label">All Sites</span>
                        <span className="org-selector-item-metric" aria-label={`${headerSites.length} sites`}>
                          {headerSites.length}
                        </span>
                      </li>
                      {filteredHeaderSites.map((s, idx) => (
                        <li
                          key={`${s.id}-${idx}`}
                          className={`org-selector-item${s.id === selectedSiteId ? ' is-selected' : ''}`}
                          onClick={() => handleSiteSelect(s)}
                        >
                          {String(s.name || '').trim() || s.id}
                        </li>
                      ))}
                      {!headerSitesLoading && filteredHeaderSites.length === 0 && (
                        <li className="org-selector-empty">No sites found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="main-header-icon-btn"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                onClick={toggleDarkMode}
              >
                {isDarkMode ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
                )}
              </button>
              <div className="main-header-notifications" ref={notificationsRef}>
                <button
                  type="button"
                  className="main-header-icon-btn main-header-icon-btn-notifications"
                  aria-label="Notifications"
                  title="Notifications"
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowTourMenu(false);
                    setShowNotifications((prev) => {
                      const nextOpen = !prev;
                      if (nextOpen) {
                        loadNotifications();
                      }
                      return nextOpen;
                    });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                  {unreadCount > 0 && (
                    <span
                      className="notification-badge"
                      aria-label={`${unreadCount} unread notifications`}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div className="notifications-dropdown">
                    <div className="notifications-header">
                      <span>Notifications</span>
                      {unreadCount > 0 ? (
                        <span className="notifications-subtitle">{unreadCount} unread</span>
                      ) : (
                        <span className="notifications-subtitle">All caught up</span>
                      )}
                    </div>
                    <ul className="notifications-list">
                      {notificationsLoading && (
                        <li className="notifications-item is-read" role="status" aria-live="polite">
                          <span className="notifications-text">Loading notifications…</span>
                        </li>
                      )}
                      {!notificationsLoading && notificationsError && (
                        <li className="notifications-item is-read" role="status" aria-live="polite">
                          <span className="notifications-text">Unable to load notifications right now.</span>
                        </li>
                      )}
                      {!notificationsLoading && !notificationsError && notifications.length === 0 && (
                        <li className="notifications-item is-read" role="status" aria-live="polite">
                          <span className="notifications-text">No notifications yet.</span>
                        </li>
                      )}
                      {!notificationsLoading && !notificationsError && notifications.map((n) => (
                        <li
                          key={n.id}
                          className={`notifications-item ${n.read ? 'is-read' : 'is-unread'}`}
                          onClick={() => handleNotificationClick(n)}
                        >
                          <span className="notifications-dot" aria-hidden="true" />
                          <span className="notifications-text">{n.message || n.title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="main-header-profile" ref={profileRef} data-tour="header-profile">
                <button
                  type="button"
                  className="main-header-icon-btn main-header-icon-btn-profile"
                  aria-label="Profile"
                  title="Profile"
                  onClick={() => {
                    setShowNotifications(false);
                    setShowProfileMenu((prev) => {
                      const next = !prev;
                      if (!next) setShowTourMenu(false);
                      return next;
                    });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                </button>
                {showProfileMenu && (
                  <div className="profile-dropdown" role="menu" aria-label="Profile menu">
                    <div className="profile-header">
                      <div className="profile-meta">
                        <div className="profile-name" title={userProfile.name || 'User'}>
                          {userProfile.name || 'User'}
                        </div>
                        {userProfile.email ? (
                          <div className="profile-email" title={userProfile.email}>
                            {userProfile.email}
                          </div>
                        ) : null}
                      </div>
                      <div className="profile-context">
                        <span className="profile-role">
                          {userProfile.role || 'User'}
                        </span>
                      </div>
                    </div>
                    <div className="profile-menu" data-tour="profile-menu">
                      <button
                        type="button"
                        className="profile-item"
                        role="menuitem"
                        data-tour="profile-settings-item"
                        onClick={() => {
                          setShowProfileMenu(false);
                          setShowTourMenu(false);
                          handleSelectPage('profileSettings');
                        }}
                      >
                        <svg
                          className="profile-item-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                        <span className="profile-item-label">Profile settings</span>
                      </button>
                      {DASHBOARD_TOURS_ENABLED ? (
                        <div className="profile-tour-block" ref={tourMenuRef}>
                          <button
                            type="button"
                            className="profile-item"
                            role="menuitem"
                            data-tour="tour-help"
                            aria-label="Choose a guided tour"
                            aria-haspopup="menu"
                            aria-expanded={showTourMenu}
                            onClick={() => {
                              // Never destroy an in-progress tour from this control —
                              // handoff steps highlight it; Next section lives on the popover.
                              setShowTourMenu((prev) => !prev);
                            }}
                          >
                            <svg
                              className="profile-item-icon"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                              <line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                            <span className="profile-item-label">Guided tours</span>
                          </button>
                          {showTourMenu ? (
                            <div className="profile-tour-submenu" role="menu" aria-label="Guided tours">
                              {tourStartOptions.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className="profile-item profile-tour-option"
                                  role="menuitem"
                                  aria-label={option.description ? `${option.label}. ${option.description}` : option.label}
                                  title={option.description || option.label}
                                  onClick={() => {
                                    setShowTourMenu(false);
                                    setShowProfileMenu(false);
                                    startTourFrom(option);
                                  }}
                                >
                                  <span className="profile-tour-option-icon" aria-hidden="true">
                                    <TourMenuIcon id={option.id} />
                                  </span>
                                  <span className="profile-item-label">{option.label}</span>
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="profile-item profile-item-danger"
                        role="menuitem"
                        onClick={async () => {
                          setShowProfileMenu(false);
                          setShowTourMenu(false);
                          if (typeof onLogout === 'function') {
                            await onLogout();
                          }
                        }}
                      >
                        <svg
                          className="profile-item-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        <span className="profile-item-label">Log out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
          <div
            className={`main-content-inner${activePage === 'dashboard' ? ' page-dashboard' : ''}${
              activePage === 'targets' ? ' page-assets' : ''
            }${
              (activePage === 'scans' || activePage === 'activeScans') ? ' page-scans' : ''
            }${
              activePage === 'risks' ? ' page-risks' : ''
            }${
              activePage === 'reports' ? ' page-reports' : ''
            }${
              activePage === 'earlyDisclosures' || activePage === 'earlyDisclosures2'
                ? ' page-early-disclosures'
                : ''
            }${activePage === 'riskDetail' ? ' page-risk-detail' : ''}${
              activePage === 'scanDetail' ? ' page-scan-detail' : ''
            }${
              activePage === 'aiIntelligence' ? ' page-ai-intelligence' : ''
            }${activePage === 'xdr' ? ' page-xdr' : ''}${
              activePage === 'securityEvents' || activePage === 'alerts' ? ' page-security-events' : ''
            }${
              activePage === 'subscriptionOrgDetail' ? ' page-subscription-org' : ''
            }${
              isAppSettingsPage(activePage) ? ' page-app-settings' : ''
            }${
              isTableFillPage(activePage) ? ' page-table-fill' : ''
            }`}
          >
            <div className="top-header" data-tour="page-heading">
              {activePage !== 'profileSettings' && activePage === 'earlyDisclosures' && (
                <div className="early-disclosures-title-block">
                  <div className="early-disclosures-title-row">
                    <h1 className="overview-title">Early Disclosure Intelligence</h1>
                  </div>
                  <div className="early-disclosures-page-subtitle-spacer" aria-hidden="true" />
                </div>
              )}
              {activePage !== 'profileSettings' && activePage === 'earlyDisclosures2' && (
                <div className="early-disclosures-title-block">
                  <div className="early-disclosures-title-row">
                    <h1 className="overview-title">{PAGE_TITLES.earlyDisclosures2}</h1>
                  </div>
                  <div className="early-disclosures-page-subtitle-spacer" aria-hidden="true" />
                </div>
              )}
              {activePage === 'subscriptionOrgDetail' && (
                <div className="subscription-org-title-block">
                  <div className="subscription-org-title-row">
                    <h1 className="overview-title">{PAGE_TITLES.subscriptionOrgDetail}</h1>
                  </div>
                  <button
                    type="button"
                    className="subscription-org-back-link"
                    data-tour="subscription-org-back"
                    onClick={() => handleSelectPage('subscription')}
                  >
                    ← back to subscription
                  </button>
                </div>
              )}
              {activePage !== 'profileSettings' && activePage === 'settingsConnectors' && (
                <div className="connectors-overview-title-block">
                  <div className="connectors-overview-title-row">
                    <h1 className="overview-title" aria-describedby={connectorsOverviewHintId}>
                      <span
                        className="connectors-settings-page-title-hoverhint"
                        tabIndex={0}
                        data-connectors-hint={CONNECTORS_SETTINGS_HINT}
                      >
                        {PAGE_TITLES.settingsConnectors}
                      </span>
                    </h1>
                    {uiAccess.canManageSettingsConnectors ? (
                      <button
                        type="button"
                        className="connectors-settings-add-btn connectors-settings-add-btn--overview page-header-cta"
                        title="Open add connector dialog"
                        aria-haspopup="dialog"
                        aria-expanded={addConnectorModalOpen}
                        aria-controls="add-connector-dialog"
                        onClick={() => openNewAddConnectorModal(connectorsScannerTab)}
                      >
                        <svg className="page-header-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        Add Connector
                      </button>
                    ) : null}
                  </div>
                  <span id={connectorsOverviewHintId} className="edi-sr-only">
                    {CONNECTORS_SETTINGS_HINT}
                  </span>
                </div>
              )}
              {activePage === 'dashboard' && (
                <div className="overview-page-title-row">
                  <div className="overview-page-title-block">
                    <h1 className="overview-title">{PAGE_TITLES.dashboard || 'Security Overview'}</h1>
                    <p className="overview-subtitle">Your security posture at a glance.</p>
                  </div>
                  {/* TEMP: Overview date-range selector hidden for now — restore when needed
                   * Keep date-range sibling inside overview-page-title-row when re-enabled.
                  <div
                    className="overview-date-range-select-wrap"
                    data-tour="overview-date-range"
                    ref={overviewDateRangeRef}
                  >
                    <button
                      type="button"
                      id="overview-date-range-select"
                      className={`overview-date-range-select${overviewDateRange !== 'all_time' ? ' is-active' : ''}${overviewDateRangeOpen ? ' is-open' : ''}`}
                      aria-label="Overview date range"
                      aria-haspopup="listbox"
                      aria-expanded={overviewDateRangeOpen}
                      onClick={() => {
                        setOverviewDateRangeOpen((open) => !open);
                        setOrgDropdownOpen(false);
                        setSiteDropdownOpen(false);
                      }}
                    >
                      <span className="overview-date-range-select-label">
                        {OVERVIEW_DATE_RANGE_OPTIONS.find((opt) => opt.value === overviewDateRange)?.label
                          || 'All time'}
                      </span>
                      <svg
                        className="overview-date-range-select-chevron"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {overviewDateRangeOpen ? (
                      <ul
                        className="overview-date-range-menu"
                        role="listbox"
                        aria-labelledby="overview-date-range-select"
                      >
                        {OVERVIEW_DATE_RANGE_OPTIONS.map((opt) => (
                          <li key={opt.value} role="none">
                            <button
                              type="button"
                              role="option"
                              aria-selected={overviewDateRange === opt.value}
                              className={`overview-date-range-menu-item${overviewDateRange === opt.value ? ' is-selected' : ''}`}
                              onClick={() => {
                                setOverviewDateRange(opt.value);
                                setOverviewDateRangeOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  */}
                </div>
              )}
              {activePage !== 'profileSettings' &&
                activePage !== 'dashboard' &&
                activePage !== 'earlyDisclosures' &&
                activePage !== 'earlyDisclosures2' &&
                activePage !== 'subscriptionOrgDetail' &&
                activePage !== 'riskDetail' &&
                activePage !== 'scanDetail' &&
                activePage !== 'settingsConnectors' &&
                activePage !== 'settingsUsersOrgs' &&
                activePage !== 'settingsSites' &&
                activePage !== 'settingsPartners' &&
                activePage !== 'settingsManageUsers' &&
                activePage !== 'reports' && (
                  <h1 className="overview-title">{PAGE_TITLES[activePage] || 'Overview'}</h1>
                )}
              {activePage === 'reports' && (
                <div className="reports-page-title-block">
                  <div className="reports-page-title-row">
                    <h1 className="overview-title">{routeReportId ? 'Report Details' : 'Reports'}</h1>
                    <div id="reports-page-header-actions" className="reports-page-header-actions" />
                  </div>
                  {routeReportId ? (
                    <button
                      type="button"
                      className="subscription-org-back-link"
                      data-tour="report-detail-back"
                      onClick={() => navigate('/app/reports')}
                    >
                      ← back to reports
                    </button>
                  ) : null}
                </div>
              )}
              {activePage === 'settingsUsersOrgs' && (
                <div className="reports-page-title-row">
                  <h1 className="overview-title">{PAGE_TITLES[activePage] || 'Overview'}</h1>
                  <div id="orgs-page-header-actions" className="reports-page-header-actions" />
                </div>
              )}
              {activePage === 'settingsSites' && (
                <div className="reports-page-title-row">
                  <h1 className="overview-title">{PAGE_TITLES[activePage] || 'Overview'}</h1>
                  <div id="sites-page-header-actions" className="reports-page-header-actions" />
                </div>
              )}
              {activePage === 'settingsManageUsers' && (
                <div className="reports-page-title-row">
                  <h1 className="overview-title">{PAGE_TITLES[activePage] || 'Overview'}</h1>
                  <div id="users-page-header-actions" className="reports-page-header-actions" />
                </div>
              )}
              {activePage === 'settingsPartners' && (
                <div className="reports-page-title-row">
                  <h1 className="overview-title">{PAGE_TITLES[activePage] || 'Overview'}</h1>
                  <div id="partners-page-header-actions" className="reports-page-header-actions" />
                </div>
              )}
              {(activePage === 'dashboard' || activePage === 'scans' || activePage === 'risks') && (
                <div className="header-actions">
                  {activePage === 'dashboard' && (
                    <div className="scan-status" data-tour="header-scan-status">
                      <button
                        type="button"
                        className="scan-status-item scan-status-item--clickable"
                        data-tour="header-scan-in-progress"
                        onClick={() => openActiveScansPage('in_progress')}
                      >
                        {inProgressScans > 0 ? (
                          <span className="scan-status-loader" aria-hidden="true" />
                        ) : null}
                        <strong className="scan-status-count">{inProgressScans}</strong> scans in progress
                      </button>
                      <span className="scan-status-divider" aria-hidden="true">|</span>
                      <button
                        type="button"
                        className="scan-status-item scan-status-item--clickable"
                        data-tour="header-scan-scheduled"
                        onClick={() => openActiveScansPage('scheduled')}
                      >
                        <strong className="scan-status-count">{scheduledScans}</strong> scheduled scans
                      </button>
                    </div>
                  )}
                  {activePage === 'risks' ? (
                    <div id="risks-page-header-actions" className="risks-page-header-actions" />
                  ) : null}
                  {(activePage === 'dashboard' || activePage === 'scans') && uiAccess.canRunScans ? (
                    <button
                      type="button"
                      className="new-scan-btn page-header-cta"
                      data-tour="scans-new-scan"
                      onClick={() => {
                        if (activePage === 'scans' && scansPageTab === 'network') {
                          setNewScanSkipPickType(true);
                          setNewScanStep('network-manager');
                        } else if (activePage === 'scans') {
                          setNewScanSkipPickType(true);
                          setNewScanStep('web-manager');
                        } else {
                          setNewScanSkipPickType(false);
                          setNewScanStep('pick-type');
                        }
                        setNewScanModalOpen(true);
                      }}
                      aria-haspopup="dialog"
                      aria-expanded={newScanModalOpen}
                      aria-controls="new-scan-flow-dialog"
                    >
                      <svg className="page-header-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      New Scan
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            <DashboardPageContentRouter
              activePage={activePage}
              pageMeta={{ isAppSettingsPage, PAGE_TITLES }}
              dashboard={{
                dataWarnings,
                summary,
                summaryLoading:
                  overviewBlocksPage(summaryQuery, scopeReady)
                  || (useClientOverviewDateFilter
                    && !useDerivedOverviewSummary
                    && overviewBlocksPage(historyQuery, scopeReady)),
                summaryLoadError: summaryQuery.isError && !summaryQuery.data
                  ? toUserFacingErrorMessage(
                      summaryQuery.error?.message,
                      'Unable to load overview metrics.'
                    )
                  : '',
                isDarkMode,
                openScansPageWithFilter,
                openRisksPageWithFilters,
                onClearUniversalSearch: () => {
                  setUniversalSearchQuery('');
                  setUniversalSearchDropdownOpen(false);
                },
                uiAccess,
                dashboardScopeQuery,
                vulnerabilities,
                vulnerabilitiesLoading: useDerivedOverviewTables
                  ? overviewBlocksPage(overviewFindingsQuery, scopeReady)
                  : overviewBlocksPage(vulnerabilitiesQuery, scopeReady),
                vulnerabilitiesLoadError: vulnerabilitiesQuery.isError
                  ? toUserFacingErrorMessage(
                      vulnerabilitiesQuery.error?.message,
                      'Unable to load open vulnerabilities.'
                    )
                  : (useDerivedOverviewTables && overviewFindingsQuery.isError
                    ? toUserFacingErrorMessage(
                        overviewFindingsQuery.error?.message,
                        'Unable to load open vulnerabilities.'
                      )
                    : ''),
                topVulnerableHosts,
                hostsLoading: useDerivedOverviewTables
                  ? overviewBlocksPage(overviewFindingsQuery, scopeReady)
                  : overviewBlocksPage(hostsQuery, scopeReady),
                hostsLoadError: hostsQuery.isError
                  ? toUserFacingErrorMessage(
                      hostsQuery.error?.message,
                      'Unable to load top vulnerable hosts.'
                    )
                  : (useDerivedOverviewTables && overviewFindingsQuery.isError
                    ? toUserFacingErrorMessage(
                        overviewFindingsQuery.error?.message,
                        'Unable to load top vulnerable hosts.'
                      )
                    : ''),
                universalSearchQuery,
                chartIncidents,
                historyLoading: overviewBlocksPage(historyQuery, scopeReady),
                historyLoadError: historyQuery.isError
                  ? toUserFacingErrorMessage(
                      historyQuery.error?.message,
                      'Unable to load incident trend data.'
                    )
                  : '',
                overviewDateRange,
              }}
              risks={{
                filterSeed: risksFilterSeed,
                orgQuery: dashboardScopeQuery,
                universalSearchQuery,
                riskId: routeRiskId,
                scannerHint: routeScannerHint,
                seedRow: riskDetailSeedRow,
                onBack: goBackToRisks,
                userProfile,
                RiskDetailPageComponent: RiskDetailPageContent,
              }}
              targets={{
                dataWarnings,
                assetsInventory,
                universalSearchQuery,
                onInventoryRefresh: async () => {
                  const data = await getInventory(null, dashboardScopeQuery);
                  setAssetsInventory(Array.isArray(data) ? data : []);
                },
              }}
              profile={{
                userProfile,
                onUserProfileChange: handleUserProfileChange,
                onLogout,
                forcedTab: profileSettingsTab,
              }}
              connectors={{
                universalSearchQuery,
                onUniversalSearchQueryChange: setUniversalSearchQuery,
                onConfigureConnector: handleConfigureConnector,
                onDeleteConnector: handleDeleteConnector,
                onTestConnector: handleTestConnector,
                connectorListRefreshKey,
                canManageConnectors: uiAccess.canManageSettingsConnectors,
                scannerTab: connectorsScannerTab,
                onScannerTabChange: setConnectorsScannerTab,
              }}
              settings={{
                userProfile,
                onUserProfileChange: handleUserProfileChange,
                openManageUsersInviteModal,
                manageUsersRefreshKey,
                onLogout,
                headerSelectedOrgId: selectedOrgId,
                headerSelectedOrgName,
              }}
              scans={{
                scansInitialTab: scansPageTab,
                consumeScansInitialTab,
                scansInitialFilter: scansPageStatusFilter,
                consumeScansInitialFilter,
                scansInitialLocation: scansFilterSeed.location === 'perimeter' || scansFilterSeed.location === 'onprem'
                  ? scansFilterSeed.location
                  : 'onprem',
                scansRefreshKey,
                dashboardScopeQuery,
                headerSelectedOrgName,
                onClearHeaderOrg: handleClearOrgSelection,
                universalSearchQuery,
                onUniversalSearchQueryChange: setUniversalSearchQuery,
                isDarkMode,
                userProfile,
                scanId: routeScanId,
                scanType: scanDetailType,
                targetLabel: scanDetailTargetLabel,
                seedRow: scanDetailSeedRow,
                onBack: goBackToScans,
              }}
              earlyDisclosures={{
                onViewGhostCveExposures: (cveId) => {
                  const q = String(cveId || '').trim();
                  setGhostExposuresFilterSeed((s) => ({ nonce: s.nonce + 1, cveQuery: q }));
                  handleSelectPage('earlyDisclosures2');
                },
                ghostExposuresFilterSeed,
                onBackToEarlyDisclosures: () => handleSelectPage('earlyDisclosures'),
                orgScopeQuery: earlyIntelScopeQuery,
                userProfile,
              }}
              subscription={{
                // Organisation list
                subscriptionOrgs,
                subscriptionOrgsLoading,
                subscriptionOrgsError,
                selectedOrg: selectedSubscriptionOrg,
                onViewPlansBilling: (org) => {
                  setSelectedSubscriptionOrg(org);
                  // Pass org via router state so it arrives atomically with the
                  // navigation, avoiding a React 18 batching race in the auto-select.
                  navigate(pageToPath('subscriptionOrgDetail'), { state: { subscriptionOrg: org } });
                },
                onBack: () => handleSelectPage('subscription'),
                // Current plan + org-level usage
                subscriptionCurrent,
                subscriptionCurrentLoading,
                subscriptionCurrentError,
                // Per-site usage
                subscriptionUsage,
                subscriptionUsageLoading,
                subscriptionUsageError,
                // Table helpers
                subscriptionSearch,
                setSubscriptionSearch,
                subscriptionSortConfig,
                toggleSubscriptionSort,
                getSubscriptionSortIcon,
                filteredSubscriptionSites,
                renderSubscriptionUsageSegments,
              }}
              support={{
                SupportPageComponent: SupportPage,
                userProfile,
                uiAccess,
              }}
              pageComponents={{
                ProfileSettingsComponent: ProfileSettings,
                ConnectorsSettingsPageComponent: ConnectorsSettingsPage,
                DeploymentSettingsPageComponent: DeploymentSettingsPage,
                UsersOrgsSettingsPageComponent: UsersOrgsSettingsPage,
                ManageUsersPageComponent: ManageUsersPage,
                PartnerManagementSettingsPageComponent: PartnerManagementSettingsPage,
                SupportPageComponent: SupportPage,
              }}
              featureComponents={{
                RisksPageComponent: RisksPageContent,
                ScansPageComponent: ScansPageContent,
                EarlyDisclosuresPageComponent: EarlyDisclosuresPage,
                GhostCveExposuresSystemPageComponent: GhostCveExposuresSystemPage,
                HealthScoreComponent: HealthScore,
                RisksDetectedComponent: RisksDetected,
                PartnerScoreCardComponent: PartnerScoreCard,
                VulnerabilitiesTableComponent: VulnerabilitiesTable,
                HeatmapChartComponent: HeatmapChart,
                AssetsTableComponent: AssetsTable,
              }}
            />
          </div>
        </div>
      </div>

      {editSiteModalOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEditSiteModal();
          }}
        >
          <div
            id="edit-site-dialog"
            className="assets-detail-modal users-orgs-org-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={editSiteDialogTitleId}
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header">
                <div className="assets-detail-modal-title" id={editSiteDialogTitleId}>
                  Edit site
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeEditSiteModal}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <form className="users-orgs-org-detail-form" onSubmit={submitEditSiteModal}>
                {editSiteModalError ? (
                  <div
                    className="profile-settings-message profile-settings-error"
                    role="alert"
                    style={{ marginBottom: 12 }}
                  >
                    {editSiteModalError}
                  </div>
                ) : null}

                {editSiteCanChangeOrg ? (
                  <div className="users-orgs-org-detail-field">
                    <label htmlFor={editSiteOrgFieldId}>Organization</label>
                    <select
                      id={editSiteOrgFieldId}
                      className="users-orgs-org-detail-input"
                      value={editSiteForm.organizationId}
                      onChange={(e) => setEditSiteForm((prev) => ({ ...prev, organizationId: e.target.value }))}
                      disabled={editSiteSubmitting || orgsLoading}
                    >
                      <option value="">Select organization</option>
                      {orgs.map((o) => {
                        const oid = getOrgIdentity(o);
                        if (!oid) return null;
                        return (
                          <option key={oid} value={oid}>
                            {String(o?.name || oid)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : null}

                <div className="users-orgs-org-detail-field">
                  <label htmlFor={editSiteNameFieldId}>Site name</label>
                  <input
                    id={editSiteNameFieldId}
                    type="text"
                    className="users-orgs-org-detail-input"
                    value={editSiteForm.siteName}
                    onChange={(e) => setEditSiteForm((prev) => ({ ...prev, siteName: e.target.value }))}
                    autoComplete="off"
                    disabled={editSiteSubmitting}
                  />
                </div>
                <div className="users-orgs-org-detail-field">
                  <label htmlFor={editSiteDescFieldId}>Description</label>
                  <input
                    id={editSiteDescFieldId}
                    type="text"
                    className="users-orgs-org-detail-input"
                    value={editSiteForm.siteDescription}
                    onChange={(e) => setEditSiteForm((prev) => ({ ...prev, siteDescription: e.target.value }))}
                    autoComplete="off"
                    disabled={editSiteSubmitting}
                  />
                </div>
                <div className="users-orgs-org-detail-field">
                  <label htmlFor={editSiteDetailsFieldId}>Location / details</label>
                  <input
                    id={editSiteDetailsFieldId}
                    type="text"
                    className="users-orgs-org-detail-input"
                    value={editSiteForm.siteDetails}
                    onChange={(e) => setEditSiteForm((prev) => ({ ...prev, siteDetails: e.target.value }))}
                    autoComplete="off"
                    disabled={editSiteSubmitting}
                  />
                </div>
                <div className="users-orgs-org-detail-field">
                  <label htmlFor={editSiteStatusFieldId}>Status</label>
                  <input
                    id={editSiteStatusFieldId}
                    type="text"
                    className="users-orgs-org-detail-input"
                    value={editSiteForm.siteStatus}
                    onChange={(e) => setEditSiteForm((prev) => ({ ...prev, siteStatus: e.target.value }))}
                    autoComplete="off"
                    disabled={editSiteSubmitting}
                  />
                </div>
                <div className="users-orgs-org-detail-check-row">
                  <input
                    id={editSiteActiveFieldId}
                    className="users-orgs-org-detail-check-input"
                    type="checkbox"
                    checked={Boolean(editSiteForm.isActive)}
                    onChange={(e) => setEditSiteForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={editSiteSubmitting}
                  />
                  <label className="users-orgs-org-detail-check-label" htmlFor={editSiteActiveFieldId}>
                    Active
                  </label>
                </div>

                <div className="users-orgs-org-detail-actions">
                  <button
                    type="submit"
                    className="users-orgs-org-detail-save-btn"
                    disabled={editSiteSubmitting || !String(editSiteForm.siteName || '').trim()}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {createSiteModalOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCreateSiteModal();
          }}
        >
          <div
            id="create-site-dialog"
            className="assets-detail-modal users-orgs-org-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={createSiteDialogTitleId}
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header">
                <div className="assets-detail-modal-title" id={createSiteDialogTitleId}>
                  Create site
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeCreateSiteModal}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <form className="users-orgs-org-detail-form" onSubmit={submitCreateSiteModal}>
                {createSiteModalError ? (
                  <div
                    className="profile-settings-message profile-settings-error"
                    role="alert"
                    style={{ marginBottom: 12 }}
                  >
                    {createSiteModalError}
                  </div>
                ) : null}
                <div className="users-orgs-org-detail-field">
                  <label htmlFor={createSiteOrgFieldId}>Organization</label>
                  <select
                    id={createSiteOrgFieldId}
                    className="users-orgs-org-detail-input"
                    value={createSiteOrganizationId}
                    onChange={(e) => setCreateSiteOrganizationId(e.target.value)}
                    disabled={createSiteSubmitting || orgsLoading}
                  >
                    <option value="">Select organization</option>
                    {orgs.map((o) => {
                      const oid = getOrgIdentity(o);
                      if (!oid) return null;
                      return (
                        <option key={oid} value={oid}>
                          {String(o?.name || oid)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="users-orgs-org-detail-field">
                  <label htmlFor={createSiteNameFieldId}>Site name</label>
                  <input
                    id={createSiteNameFieldId}
                    type="text"
                    className="users-orgs-org-detail-input"
                    value={createSiteName}
                    onChange={(e) => setCreateSiteName(e.target.value)}
                    autoComplete="off"
                    disabled={createSiteSubmitting}
                    placeholder="e.g. Corp HQ"
                  />
                </div>
                <div className="users-orgs-org-detail-field">
                  <label htmlFor={createSiteDescFieldId}>Description (optional)</label>
                  <input
                    id={createSiteDescFieldId}
                    type="text"
                    className="users-orgs-org-detail-input"
                    value={createSiteDescription}
                    onChange={(e) => setCreateSiteDescription(e.target.value)}
                    autoComplete="off"
                    disabled={createSiteSubmitting}
                    placeholder="Short note"
                  />
                </div>
                <div className="users-orgs-org-detail-actions">
                  <button
                    type="submit"
                    className="users-orgs-org-detail-save-btn"
                    disabled={
                      createSiteSubmitting ||
                      !String(createSiteName || '').trim() ||
                      !String(createSiteOrganizationId || '').trim()
                    }
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {newScanModalOpen ? (
        <div
          className="new-scan-modal-overlay"
          role="presentation"
        >
          {/* Wide breakpoint class only when a manager step is active (720px cap). */}
          <div
            id="new-scan-flow-dialog"
            className={`new-scan-modal new-scan-modal--steps${
              newScanStep === 'network-manager' || newScanStep === 'web-manager'
                ? ' new-scan-modal--steps-network'
                : ''
            }`}
            data-tour="new-scan-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={
              newScanStep === 'network-manager'
                ? 'network-scan-manager-title'
                : newScanStep === 'web-manager'
                  ? 'web-scan-manager-title'
                  : 'new-scan-modal-title'
            }
          >
            <div className="new-scan-modal-steps-viewport">
              <div
                className={`new-scan-modal-steps-track${
                  newScanStep === 'network-manager' || newScanStep === 'web-manager' ? ' is-network-step' : ''
                }`}
              >
                <div
                  className="new-scan-modal-step new-scan-modal-step--pick"
                  inert={newScanStep !== 'pick-type' ? true : undefined}
                >
                  <div className="new-scan-modal-step-inner">
                    <div className="new-scan-modal-header">
                      <h2 id="new-scan-modal-title" className="ghost-cve-section-title new-scan-modal-title">
                        Start a new scan
                      </h2>
                      <button type="button" className="new-scan-modal-close" aria-label="Close" onClick={closeNewScanModal}>
                        ✕
                      </button>
                    </div>
                    <div className="new-scan-modal-options" role="group" aria-label="Scan type">
                      <button
                        type="button"
                        className="new-scan-modal-option"
                        data-tour="new-scan-web"
                        onClick={() => setNewScanStep('web-manager')}
                      >
                        <span className="new-scan-modal-option-label">Web scan</span>
                        <span className="new-scan-modal-option-desc">Application / URL scanning</span>
                      </button>
                      <button
                        type="button"
                        className="new-scan-modal-option"
                        data-tour="new-scan-network"
                        onClick={() => setNewScanStep('network-manager')}
                      >
                        <span className="new-scan-modal-option-label">Network scan</span>
                        <span className="new-scan-modal-option-desc">Infrastructure / network discovery</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className="new-scan-modal-step new-scan-modal-step--network"
                  inert={newScanStep === 'pick-type' ? true : undefined}
                >
                  {/* Exactly one manager mounts at a time; `null` on pick-type avoids a duplicate off-screen form. */}
                  {newScanStep === 'web-manager' ? (
                    <WebApplicationScanManagerModalBody
                      key={scanManagerFormKey}
                      showBack={!newScanSkipPickType}
                      onBack={() => {
                        if (newScanSkipPickType) closeNewScanModal();
                        else setNewScanStep('pick-type');
                      }}
                      onClose={closeNewScanModal}
                      onSubmit={handleWebScanManagerSubmit}
                      timePickerTheme={isDarkMode ? 'dark' : 'light'}
                      userTimezone={userProfile?.timezone || getPreferredTimezoneDefault()}
                      orgQuery={dashboardScopeQuery}
                    />
                  ) : newScanStep === 'network-manager' ? (
                    <NetworkScanManagerModalBody
                      key={scanManagerFormKey}
                      showBack={!newScanSkipPickType}
                      onBack={() => {
                        if (newScanSkipPickType) closeNewScanModal();
                        else setNewScanStep('pick-type');
                      }}
                      onClose={closeNewScanModal}
                      onSubmit={handleNetworkScanManagerSubmit}
                      timePickerTheme={isDarkMode ? 'dark' : 'light'}
                      userTimezone={userProfile?.timezone || getPreferredTimezoneDefault()}
                      orgQuery={dashboardScopeQuery}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {manageUsersInviteModalOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeManageUsersInviteModal();
          }}
        >
          <div
            className={`assets-detail-modal users-orgs-user-detail-modal users-orgs-add-invite-user-modal${
              manageUsersInviteStep === 'choice' ? ' users-orgs-add-invite-user-modal--choice' : ''
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-users-add-invite-title"
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header users-orgs-add-invite-modal-header">
                <div className="users-orgs-add-invite-header-left">
                  {manageUsersInviteStep !== 'choice' ? (
                    <button
                      type="button"
                      className="users-orgs-add-invite-step-back assets-detail-modal-close"
                      onClick={() => {
                        resetManageUsersInviteDraft();
                        setManageUsersInviteStep('choice');
                      }}
                      aria-label="Back"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                <div className="assets-detail-modal-title users-orgs-add-invite-modal-title" id="manage-users-add-invite-title">
                  {manageUsersInviteStep === 'choice'
                    ? 'Add or Invite User'
                    : manageUsersInviteStep === 'add'
                      ? 'Add a new user'
                      : 'Invite new user'}
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    onClick={closeManageUsersInviteModal}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="users-orgs-add-invite-user-body">
                {manageUsersInviteError ? (
                  <p className="profile-settings-message profile-settings-error" role="alert" style={{ marginBottom: 8 }}>
                    {manageUsersInviteError}
                  </p>
                ) : null}

                {manageUsersInviteStep === 'choice' ? (
                  <div className="new-scan-modal-options" role="group" aria-label="How to add a user">
                    <button
                      type="button"
                      className="new-scan-modal-option"
                      onClick={() => {
                        resetManageUsersInviteDraft();
                        setManageUsersInviteStep('add');
                      }}
                    >
                      <span className="new-scan-modal-option-label">Add user</span>
                      <span className="new-scan-modal-option-desc">Create the account now</span>
                    </button>
                    <button
                      type="button"
                      className="new-scan-modal-option"
                      onClick={() => {
                        resetManageUsersInviteDraft();
                        setManageUsersInviteStep('invite');
                      }}
                    >
                      <span className="new-scan-modal-option-label">Invite user</span>
                      <span className="new-scan-modal-option-desc">Send an email invitation</span>
                    </button>
                  </div>
                ) : (
                  <form className="users-orgs-user-detail-form users-orgs-add-invite-form" onSubmit={submitManageUsersInviteModal}>
                    <div className="users-orgs-add-invite-form-scroll">
                      <section className="users-orgs-add-invite-card" aria-label="User details">
                        <div className="users-orgs-add-invite-card-body">
                          <div className="users-orgs-name-split">
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor="manage-users-add-first-name">First name</label>
                              <input
                                id="manage-users-add-first-name"
                                type="text"
                                className={`users-orgs-user-detail-input${manageUsersInviteFieldErrors.firstName ? ' is-invalid' : ''}`}
                                value={manageUsersInviteForm.firstName}
                                onChange={(e) => {
                                  setManageUsersInviteFieldErrors((prev) =>
                                    prev.firstName ? { ...prev, firstName: '' } : prev
                                  );
                                  setManageUsersInviteForm((f) => ({ ...f, firstName: e.target.value }));
                                }}
                                placeholder="First name"
                                autoComplete="given-name"
                                aria-invalid={Boolean(manageUsersInviteFieldErrors.firstName)}
                              />
                              {manageUsersInviteFieldErrors.firstName ? (
                                <p className="connector-field-error" role="alert">
                                  {manageUsersInviteFieldErrors.firstName}
                                </p>
                              ) : null}
                            </div>
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor="manage-users-add-last-name">Last name</label>
                              <input
                                id="manage-users-add-last-name"
                                type="text"
                                className={`users-orgs-user-detail-input${manageUsersInviteFieldErrors.lastName ? ' is-invalid' : ''}`}
                                value={manageUsersInviteForm.lastName}
                                onChange={(e) => {
                                  setManageUsersInviteFieldErrors((prev) =>
                                    prev.lastName ? { ...prev, lastName: '' } : prev
                                  );
                                  setManageUsersInviteForm((f) => ({ ...f, lastName: e.target.value }));
                                }}
                                placeholder="Last name"
                                autoComplete="family-name"
                                aria-invalid={Boolean(manageUsersInviteFieldErrors.lastName)}
                              />
                              {manageUsersInviteFieldErrors.lastName ? (
                                <p className="connector-field-error" role="alert">
                                  {manageUsersInviteFieldErrors.lastName}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="users-orgs-user-detail-field">
                            <label htmlFor="manage-users-add-email">Email</label>
                            <input
                              id="manage-users-add-email"
                              type="email"
                              className={`users-orgs-user-detail-input${manageUsersInviteFieldErrors.email ? ' is-invalid' : ''}`}
                              value={manageUsersInviteForm.email}
                              onChange={(e) => {
                                setManageUsersInviteFieldErrors((prev) =>
                                  prev.email ? { ...prev, email: '' } : prev
                                );
                                setManageUsersInviteForm((f) => ({ ...f, email: e.target.value }));
                              }}
                              placeholder="Email"
                              aria-invalid={Boolean(manageUsersInviteFieldErrors.email)}
                            />
                            {manageUsersInviteFieldErrors.email ? (
                              <p className="connector-field-error" role="alert">
                                {manageUsersInviteFieldErrors.email}
                              </p>
                            ) : null}
                          </div>
                          {manageUsersInviteStep === 'add' ? (
                            <div className="users-orgs-user-detail-field">
                              <label htmlFor="manage-users-add-password">Temporary password</label>
                              <input
                                id="manage-users-add-password"
                                type="password"
                                className={`users-orgs-user-detail-input${manageUsersInviteFieldErrors.password ? ' is-invalid' : ''}`}
                                value={manageUsersInviteForm.password}
                                onChange={(e) => {
                                  setManageUsersInviteFieldErrors((prev) =>
                                    prev.password ? { ...prev, password: '' } : prev
                                  );
                                  setManageUsersInviteForm((f) => ({ ...f, password: e.target.value }));
                                }}
                                placeholder="Temporary password"
                                aria-invalid={Boolean(manageUsersInviteFieldErrors.password)}
                              />
                              {manageUsersInviteFieldErrors.password ? (
                                <p className="connector-field-error" role="alert">
                                  {manageUsersInviteFieldErrors.password}
                                </p>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </section>
                      <UserScopeAssignFields
                        organizations={manageUsersInviteOrgs}
                        sites={manageUsersInviteSites}
                        allOrganizationsAccess={manageUsersInviteForm.allOrganizationsAccess}
                        allSitesAccess={manageUsersInviteForm.allSitesAccess}
                        organizationAccessUuids={manageUsersInviteForm.organizationAccessUuids}
                        siteAccessUuids={manageUsersInviteForm.siteAccessUuids}
                        orgSearch={manageUsersInviteOrgSearch}
                        siteSearch={manageUsersInviteSiteSearch}
                        onOrgSearchChange={setManageUsersInviteOrgSearch}
                        onSiteSearchChange={setManageUsersInviteSiteSearch}
                        orgSearchId="manage-users-add-org-search"
                        siteSearchId="manage-users-add-site-search"
                        disabled={manageUsersInviteSubmitting}
                        onChange={(patch) => setManageUsersInviteForm((f) => ({ ...f, ...patch }))}
                      />
                      <section className="users-orgs-add-invite-card" aria-labelledby="manage-users-role-heading">
                        <div className="users-orgs-add-invite-card-head">
                          <h3 id="manage-users-role-heading" className="users-orgs-add-invite-card-title">
                            Role
                          </h3>
                          <p className="users-orgs-org-assign-hint">Choose the access level for this user.</p>
                        </div>
                        <div className="users-orgs-add-invite-card-body">
                          <div className="users-orgs-user-detail-field">
                            <select
                              id="manage-users-add-role"
                              className="users-orgs-user-detail-input users-orgs-user-detail-select"
                              aria-labelledby="manage-users-role-heading"
                              value={manageUsersInviteForm.role}
                              onChange={(e) => setManageUsersInviteForm((f) => ({ ...f, role: e.target.value }))}
                            >
                              {USER_DETAIL_ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </section>
                    </div>
                    <div className="users-orgs-user-detail-actions">
                      <button
                        type="submit"
                        className="users-orgs-user-detail-save-btn"
                        disabled={
                          manageUsersInviteSubmitting ||
                          !(
                            String(manageUsersInviteForm.firstName || '').trim() ||
                            String(manageUsersInviteForm.lastName || '').trim() ||
                            String(manageUsersInviteForm.email || '').trim() ||
                            String(manageUsersInviteForm.password || '').trim()
                          )
                        }
                      >
                        {manageUsersInviteStep === 'add' ? 'Create user' : 'Send invite'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {addConnectorModalOpen ? (
        <div
          className="new-scan-modal-overlay"
          role="presentation"
        >
          <div
            id="add-connector-dialog"
            className={`new-scan-modal connector-add-modal${
              addConnectorStep === 'network-openvas' || addConnectorStep === 'web-zap'
                ? ' connector-add-modal--openvas'
                : ''
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={
              addConnectorStep === 'network-openvas'
                ? 'add-connector-openvas-title'
                : addConnectorStep === 'web-zap'
                  ? 'add-connector-zap-title'
                  : 'add-connector-modal-title'
            }
            data-tour="settings-connectors-modal"
            aria-describedby={
              addConnectorStep === 'pick-scanner' ? 'add-connector-modal-desc' : undefined
            }
          >
            {addConnectorStep === 'pick-scanner' ? (
              <>
                <div className="new-scan-modal-header">
                  <h2 id="add-connector-modal-title" className="ghost-cve-section-title new-scan-modal-title">
                    Add Connector
                  </h2>
                  <button
                    type="button"
                    className="new-scan-modal-close"
                    aria-label="Close"
                    onClick={closeAddConnectorModal}
                  >
                    ✕
                  </button>
                </div>
                <p id="add-connector-modal-desc" className="new-scan-modal-hint">
                  Choose the scanner type for this connector.
                </p>
                <div className="new-scan-modal-options" role="group" aria-label="Scanner connector type">
                  <button
                    type="button"
                    className="new-scan-modal-option"
                    onClick={() => {
                      const session = (connectorModalSessionRef.current += 1);
                      const initialZap = getZapConnectorInitialState();
                      setZapConnectorDraft(initialZap);
                      setZapConnectorSnapshot(initialZap);
                      setZapConnectorEditId(null);
                      setZapConnectorIsEditing(true);
                      setZapConnectorSaveError('');
                      setZapConnectorFieldErrors({ name: '', rportClient: '' });
                      setShowZapApiKey(false);
                      setZapAssignmentSearch('');
                      setAddConnectorStep('web-zap');
                      loadZapAssignmentSites([]).then((rows) => {
                        if (connectorModalSessionRef.current !== session) return;
                        setZapAssignmentSnapshot(rows.map((row) => ({ ...row })));
                      });
                    }}
                  >
                    <span className="new-scan-modal-option-label">Web Scanner</span>
                    <span className="new-scan-modal-option-desc">Application and URL scanning</span>
                  </button>
                  <button
                    type="button"
                    className="new-scan-modal-option"
                    onClick={() => {
                      connectorModalSessionRef.current += 1;
                      setOpenvasConnectorEditId(null);
                      setAddConnectorStep('network-openvas');
                    }}
                  >
                    <span className="new-scan-modal-option-label">Network Scanner</span>
                    <span className="new-scan-modal-option-desc">Infrastructure and network discovery</span>
                  </button>
                </div>
                <p className="connector-add-modal-note">{CONNECTORS_SETTINGS_HINT}</p>
              </>
            ) : addConnectorStep === 'network-openvas' ? (
              <NetworkScannerSettingsForm
                key={openvasConnectorEditId || 'new-network-scanner'}
                connectorId={openvasConnectorEditId}
                canManage={uiAccess.canManageSettingsConnectors}
                onClose={closeAddConnectorModal}
                onSaved={() => setConnectorListRefreshKey((k) => k + 1)}
              />
            ) : (
              <>
                <div className="new-scan-modal-header connector-openvas-header-row connector-zap-modal-header">
                  <div className="connector-zap-header-title-block">
                    <h2 id="add-connector-zap-title" className="ghost-cve-section-title new-scan-modal-title">
                      ZAP Setting
                    </h2>
                    <div
                      className={`connector-zap-header-trailing${
                        zapConnectorDraft.enabled
                          ? ' connector-zap-header-trailing--on'
                          : ' connector-zap-header-trailing--off'
                      }`}
                      data-tour="settings-connectors-modal-enable"
                    >
                      <span className="connector-zap-enable-label">
                        {zapConnectorDraft.enabled ? 'Connector Enabled' : 'Connector Disabled'}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        className={`connector-zap-enable-switch${
                          zapConnectorDraft.enabled ? ' is-on' : ' is-off'
                        }`}
                        aria-checked={zapConnectorDraft.enabled}
                        aria-label={
                          zapConnectorDraft.enabled
                            ? 'Connector enabled. Click to turn off.'
                            : 'Connector disabled. Click to turn on.'
                        }
                        disabled={!zapConnectorIsEditing || !uiAccess.canManageSettingsConnectors}
                        onClick={() => {
                          if (!zapConnectorIsEditing || !uiAccess.canManageSettingsConnectors) return;
                          setZapConnectorDraft((p) => ({ ...p, enabled: !p.enabled }));
                        }}
                      >
                        <span className="connector-zap-enable-switch-thumb" aria-hidden />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="new-scan-modal-close"
                    aria-label="Close"
                    onClick={closeAddConnectorModal}
                  >
                    ✕
                  </button>
                </div>
                <form
                  className="connector-openvas-form"
                  id="connector-zap-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!zapConnectorIsEditing || zapConnectorSaving) return;
                    if (!uiAccess.canManageSettingsConnectors) {
                      setZapConnectorFieldErrors({ name: '', rportClient: '' });
                      setZapConnectorSaveError('You have read-only connector access. Save is not allowed for your role.');
                      return;
                    }
                    const name = String(zapConnectorDraft.name || '').trim();
                    const rportClient = String(zapConnectorDraft.rportClient || '').trim();
                    const hasAnyText = Boolean(
                      name ||
                        rportClient ||
                        String(zapConnectorDraft.apiKey || '').trim() ||
                        String(zapConnectorDraft.port || '').trim()
                    );
                    if (!zapConnectorEditId && !hasAnyText) return;
                    if (!name || !rportClient) {
                      setZapConnectorSaveError('');
                      setZapConnectorFieldErrors({
                        name: name ? '' : 'Required',
                        rportClient: rportClient ? '' : 'Required',
                      });
                      requestAnimationFrame(() => {
                        const el = document.getElementById(name ? 'zap-rport-client' : 'zap-name');
                        el?.focus?.();
                        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
                      });
                      return;
                    }
                    setZapConnectorSaving(true);
                    setZapConnectorSaveError('');
                    setZapConnectorFieldErrors({ name: '', rportClient: '' });
                    try {
                      const payload = {
                        scanner_name: name,
                        rport_client_name: rportClient,
                        zap_port: parseInt(zapConnectorDraft.port, 10) || 8090,
                        enabled: zapConnectorDraft.enabled,
                        site_uuids: zapAssignmentRows
                          .filter((r) => r.enabled)
                          .map((r) => r.id),
                      };
                      if (zapConnectorDraft.apiKey) {
                        payload.zap_api_key = zapConnectorDraft.apiKey;
                      }
                      payload.ssh_username = String(zapConnectorDraft.sshUser || '').trim();
                      if (zapConnectorDraft.sshPassword) {
                        payload.ssh_password = zapConnectorDraft.sshPassword;
                      }
                      if (uiAccess.isSuperuser) {
                        if (zapConnectorDraft.poolScope === 'DEDICATED' && !zapConnectorDraft.dedicatedOrg) {
                          setZapConnectorSaving(false);
                          setZapConnectorSaveError('Choose the organization that owns this dedicated scanner.');
                          return;
                        }
                        payload.pool_scope = zapConnectorDraft.poolScope;
                        payload.dedicated_organization =
                          zapConnectorDraft.poolScope === 'DEDICATED' ? zapConnectorDraft.dedicatedOrg : null;
                      }
                      if (zapConnectorEditId) {
                        await updateZapConnector(zapConnectorEditId, payload);
                      } else {
                        await createZapConnector(payload);
                      }
                      setConnectorListRefreshKey((k) => k + 1);
                      closeAddConnectorModal();
                    } catch (err) {
                      if (isPermissionDeniedError(err)) {
                        setZapConnectorSaveError('You have read-only connector access. Save is not allowed for your role.');
                      } else {
                        setZapConnectorSaveError(err?.message || 'Failed to save connector.');
                      }
                    } finally {
                      setZapConnectorSaving(false);
                    }
                  }}
                  noValidate
                >
                  <div className="connector-openvas-form-scroll" data-tour="settings-connectors-modal-fields">
                    <section className="settings-modal-card" aria-label="Connection details">
                      <div className="settings-modal-card-body">
                        <div className="connector-openvas-field">
                          <label className="ghost-exposure-sys-field-label" htmlFor="zap-name">
                            Name
                          </label>
                          {!zapConnectorIsEditing ? (
                            <p className="connector-openvas-readonly" id="zap-name">
                              {emptyDisplayValue(zapConnectorDraft.name)}
                            </p>
                          ) : (
                            <>
                              <input
                                id="zap-name"
                                type="text"
                                className={`ghost-exposure-sys-input${zapConnectorFieldErrors.name ? ' is-invalid' : ''}`}
                                placeholder="Name for the ZAP server"
                                title="Name for the ZAP server"
                                autoComplete="off"
                                aria-invalid={Boolean(zapConnectorFieldErrors.name)}
                                aria-describedby={zapConnectorFieldErrors.name ? 'zap-name-error' : undefined}
                                value={zapConnectorDraft.name}
                                onChange={(e) => {
                                  setZapConnectorFieldErrors((prev) =>
                                    prev.name ? { ...prev, name: '' } : prev
                                  );
                                  setZapConnectorDraft((p) => ({
                                    ...p,
                                    name: sanitizeZapConnectorField(e.target.value),
                                  }));
                                }}
                              />
                              {zapConnectorFieldErrors.name ? (
                                <p id="zap-name-error" className="connector-field-error" role="alert">
                                  {zapConnectorFieldErrors.name}
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>

                        <div className="connector-openvas-field">
                          <span className="ghost-exposure-sys-field-label" id="zap-label-api-key">
                            ZAP API key
                          </span>
                          {!zapConnectorIsEditing ? (
                            <p className="connector-openvas-readonly" aria-labelledby="zap-label-api-key">
                              {zapConnectorDraft.apiKeySet
                                ? 'Stored on the connector. Edit to replace.'
                                : '—'}
                            </p>
                          ) : (
                            <div className="connector-openvas-password-wrap">
                              <input
                                type={showZapApiKey ? 'text' : 'password'}
                                className="ghost-exposure-sys-input connector-openvas-input--password"
                                placeholder={
                                  zapConnectorEditId
                                    ? 'Enter a new key to replace the stored value'
                                    : 'Skip ZAP API Key if not enabled'
                                }
                                title="Skip ZAP API Key if not enabled"
                                autoComplete="new-password"
                                aria-labelledby="zap-label-api-key"
                                value={zapConnectorDraft.apiKey}
                                onChange={(e) =>
                                  setZapConnectorDraft((p) => ({
                                    ...p,
                                    apiKey: sanitizeZapConnectorField(e.target.value),
                                  }))
                                }
                              />
                              <button
                                type="button"
                                className="connector-openvas-password-toggle"
                                onClick={() => setShowZapApiKey((v) => !v)}
                                aria-label={showZapApiKey ? 'Hide ZAP API key' : 'Show ZAP API key'}
                                aria-pressed={showZapApiKey}
                              >
                                {showZapApiKey ? (
                                  <svg
                                    className="connector-openvas-password-toggle-icon"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.25"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                  >
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                ) : (
                                  <svg
                                    className="connector-openvas-password-toggle-icon"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.25"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                  >
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="connector-openvas-field">
                          <label className="ghost-exposure-sys-field-label" htmlFor="zap-rport-client">
                            RPort client (scanner VM)
                          </label>
                          {!zapConnectorIsEditing ? (
                            <p className="connector-openvas-readonly" id="zap-rport-client">
                              {emptyDisplayValue(zapConnectorDraft.rportClient)}
                            </p>
                          ) : (
                            <>
                              <input
                                id="zap-rport-client"
                                type="text"
                                className={`ghost-exposure-sys-input${zapConnectorFieldErrors.rportClient ? ' is-invalid' : ''}`}
                                placeholder="The scanner VM's client name in RPort (e.g. xs-zap-01)"
                                title="Both ZAP daemons on one VM (8090/8091) use the same RPort client"
                                autoComplete="off"
                                aria-invalid={Boolean(zapConnectorFieldErrors.rportClient)}
                                aria-describedby={zapConnectorFieldErrors.rportClient ? 'zap-rport-client-error' : undefined}
                                value={zapConnectorDraft.rportClient}
                                onChange={(e) => {
                                  setZapConnectorFieldErrors((prev) =>
                                    prev.rportClient ? { ...prev, rportClient: '' } : prev
                                  );
                                  setZapConnectorDraft((p) => ({
                                    ...p,
                                    rportClient: sanitizeZapConnectorField(e.target.value),
                                  }));
                                }}
                              />
                              {zapConnectorFieldErrors.rportClient ? (
                                <p id="zap-rport-client-error" className="connector-field-error" role="alert">
                                  {zapConnectorFieldErrors.rportClient}
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>

                        <div className="connector-openvas-field">
                          <label className="ghost-exposure-sys-field-label" htmlFor="zap-api-port">
                            ZAP API port
                          </label>
                          {!zapConnectorIsEditing ? (
                            <p className="connector-openvas-readonly" id="zap-api-port">
                              {emptyDisplayValue(zapConnectorDraft.port)}
                            </p>
                          ) : (
                            <input
                              id="zap-api-port"
                              type="text"
                              inputMode="numeric"
                              className="ghost-exposure-sys-input"
                              placeholder="ZAP API port on the scanner VM (8090 or 8091)"
                              title="ZAP API port on the scanner VM (8090 or 8091)"
                              autoComplete="off"
                              value={zapConnectorDraft.port}
                              onChange={(e) =>
                                setZapConnectorDraft((p) => ({
                                  ...p,
                                  port: sanitizeZapConnectorField(e.target.value).replace(/[^\d]/g, ''),
                                }))
                              }
                            />
                          )}
                        </div>

                        <div className="connector-openvas-field">
                          <label className="ghost-exposure-sys-field-label" htmlFor="zap-ssh-user">
                            SSH user
                          </label>
                          {!zapConnectorIsEditing ? (
                            <p className="connector-openvas-readonly" id="zap-ssh-user">
                              {emptyDisplayValue(zapConnectorDraft.sshUser)}
                            </p>
                          ) : (
                            <input
                              id="zap-ssh-user"
                              type="text"
                              className="ghost-exposure-sys-input"
                              placeholder="SSH user on the scanner host"
                              autoComplete="off"
                              value={zapConnectorDraft.sshUser}
                              onChange={(e) =>
                                setZapConnectorDraft((p) => ({
                                  ...p,
                                  sshUser: sanitizeZapConnectorField(e.target.value),
                                }))
                              }
                            />
                          )}
                        </div>

                        {!zapConnectorIsEditing ? (
                          <div className="connector-openvas-field">
                            <span className="ghost-exposure-sys-field-label">SSH password</span>
                            <p className="connector-openvas-readonly">
                              {zapConnectorDraft.sshPasswordSet
                                ? 'Stored on the connector. Edit to replace.'
                                : '—'}
                            </p>
                          </div>
                        ) : (
                          <ConnectorPasswordField
                            id="zap-ssh-pass"
                            label="SSH password"
                            value={zapConnectorDraft.sshPassword}
                            onChange={(value) =>
                              setZapConnectorDraft((p) => ({
                                ...p,
                                sshPassword: sanitizeZapConnectorField(value),
                              }))
                            }
                            revealed={showZapSshPassword}
                            onToggleReveal={() => setShowZapSshPassword((v) => !v)}
                            placeholder={
                              zapConnectorDraft.sshPasswordSet
                                ? 'Enter a new password to replace the stored value'
                                : 'Password'
                            }
                          />
                        )}

                        <div className="connector-openvas-field">
                          <label className="ghost-exposure-sys-field-label" htmlFor="zap-pool-scope">
                            Scanner pool
                          </label>
                          {!zapConnectorIsEditing || !uiAccess.isSuperuser ? (
                            <p className="connector-openvas-readonly" id="zap-pool-scope">
                              {zapConnectorDraft.poolScope === 'DEDICATED'
                                ? `Dedicated to ${zapConnectorDraft.dedicatedOrgName || 'one organization'}`
                                : 'Public pool (shared by all customers)'}
                            </p>
                          ) : (
                            <select
                              id="zap-pool-scope"
                              className="ghost-exposure-sys-input"
                              value={zapConnectorDraft.poolScope}
                              onChange={(e) =>
                                setZapConnectorDraft((p) => ({
                                  ...p,
                                  poolScope: e.target.value === 'DEDICATED' ? 'DEDICATED' : 'PUBLIC',
                                }))
                              }
                            >
                              <option value="PUBLIC">Public pool (shared by all customers)</option>
                              <option value="DEDICATED">Dedicated to one organization</option>
                            </select>
                          )}
                        </div>

                        {zapConnectorIsEditing && uiAccess.isSuperuser && zapConnectorDraft.poolScope === 'DEDICATED' ? (
                          <div className="connector-openvas-field">
                            <label className="ghost-exposure-sys-field-label" htmlFor="zap-dedicated-org">
                              Organization
                            </label>
                            <select
                              id="zap-dedicated-org"
                              className="ghost-exposure-sys-input"
                              value={zapConnectorDraft.dedicatedOrg}
                              onChange={(e) =>
                                setZapConnectorDraft((p) => ({ ...p, dedicatedOrg: e.target.value }))
                              }
                            >
                              <option value="">Select the owning organization</option>
                              {zapPoolOrgOptions.map((org) => (
                                <option key={org.value} value={org.value}>
                                  {org.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : null}
                      </div>
                    </section>

                    <section className="settings-modal-card connector-openvas-assignment" data-tour="settings-connectors-modal-assignment" aria-labelledby="zap-assignment-label">
                      <div className="settings-modal-card-head">
                        <h3 id="zap-assignment-label" className="settings-modal-card-title">
                          Scanner assignment
                        </h3>
                        <p className="settings-modal-card-hint">
                          Selected organizations and sites are scanned by this connector.
                        </p>
                      </div>
                      <div className="settings-modal-card-body">
                        <div className="connector-openvas-assign-field">
                          {zapConnectorIsEditing ? (
                            <input
                              type="search"
                              className="ghost-exposure-sys-input connector-openvas-assign-search"
                              value={zapAssignmentSearch}
                              onChange={(e) =>
                                setZapAssignmentSearch(sanitizeUniversalSearchInput(e.target.value))
                              }
                              placeholder="Search organization or site"
                              autoComplete="off"
                              aria-label="Search scanner assignment"
                              disabled={zapSitesLoading}
                            />
                          ) : null}
                          <div
                            className="connector-openvas-assign-list"
                            role="group"
                            aria-labelledby="zap-assignment-label"
                            aria-busy={zapSitesLoading}
                          >
                            {zapSitesLoading ? (
                              <p className="connector-openvas-assign-empty" role="status">
                                Loading sites…
                              </p>
                            ) : zapAssignmentRows.length === 0 ? (
                              <p className="connector-openvas-assign-empty">
                                No organizations or sites available to assign.
                              </p>
                            ) : !zapConnectorIsEditing &&
                              zapAssignmentRows.filter((row) => row.enabled).length === 0 ? (
                              <p className="connector-openvas-assign-empty">
                                No sites assigned yet.
                              </p>
                            ) : zapConnectorIsEditing && zapAssignmentPrepared.length === 0 ? (
                              <p className="connector-openvas-assign-empty">
                                No organizations or sites match that search.
                              </p>
                            ) : (
                              (zapConnectorIsEditing
                                ? zapAssignmentPrepared
                                : zapAssignmentRows.filter((row) => row.enabled)
                              ).map((row) => (
                                <label key={row.id} className="connector-openvas-assign-option">
                                  <input
                                    type="checkbox"
                                    checked={row.enabled}
                                    disabled={!zapConnectorIsEditing}
                                    onChange={(e) => {
                                      if (!zapConnectorIsEditing) return;
                                      setZapAssignmentRows((prev) =>
                                        prev.map((r) =>
                                          r.id === row.id ? { ...r, enabled: e.target.checked } : r
                                        )
                                      );
                                    }}
                                  />
                                  <span>
                                    <strong title={row.org}>{row.org}</strong>
                                    <span title={row.site}>{row.site}</span>
                                  </span>
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className="connector-openvas-form-actions" data-tour="settings-connectors-modal-save">
                    {zapConnectorSaveError ? (
                      <p className="profile-settings-message profile-settings-error" role="alert" style={{ marginBottom: 8 }}>
                        {zapConnectorSaveError}
                      </p>
                    ) : null}
                    {!zapConnectorIsEditing ? (
                      <button
                        type="button"
                        className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary"
                        onClick={() => {
                          if (!uiAccess.canManageSettingsConnectors) return;
                          setZapConnectorSaveError('');
                          setZapConnectorFieldErrors({ name: '', rportClient: '' });
                          setZapConnectorIsEditing(true);
                        }}
                        disabled={!uiAccess.canManageSettingsConnectors}
                      >
                        Edit
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary"
                        aria-disabled={
                          zapConnectorSaving ||
                          (!zapConnectorEditId &&
                            !(
                              String(zapConnectorDraft.name || '').trim() ||
                              String(zapConnectorDraft.rportClient || '').trim() ||
                              String(zapConnectorDraft.apiKey || '').trim() ||
                              String(zapConnectorDraft.port || '').trim()
                            ))
                        }
                        disabled={
                          zapConnectorSaving ||
                          (!zapConnectorEditId &&
                            !(
                              String(zapConnectorDraft.name || '').trim() ||
                              String(zapConnectorDraft.rportClient || '').trim() ||
                              String(zapConnectorDraft.apiKey || '').trim() ||
                              String(zapConnectorDraft.port || '').trim()
                            ))
                        }
                      >
                        {zapConnectorEditId ? 'Update' : 'Save'}
                      </button>
                    )}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      ) : null}

      {connectorDeleteConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeConnectorDeleteConfirm();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete connector"
            aria-describedby="connector-delete-confirm-desc"
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeConnectorDeleteConfirm}
              aria-label="Close"
              disabled={connectorDeleteBusy}
            >
              ✕
            </button>
            <p id="connector-delete-confirm-desc" className="assets-exclude-confirm-desc">
              You are about to permanently delete connector{' '}
              <strong>{connectorDeleteConfirm.name}</strong>.
              <span className="assets-exclude-confirm-warning">
                This action cannot be undone.
              </span>
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDeleteConnector}
                disabled={connectorDeleteBusy}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <MfaEnrollmentPrompt
        open={mfaEnrollmentOpen}
        onEnable={() => {
          setProfileSettingsTab('account_security');
          handleSelectPage('profileSettings');
          window.dispatchEvent(new CustomEvent('xspectre:tour-profile-tab', {
            detail: { tab: 'account_security' },
          }));
        }}
      />
    </div>
  );
}
